// SeaMap.jsx
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { renderToStaticMarkup } from 'react-dom/server';
import {
	MapContainer,
	TileLayer,
	WMSTileLayer,
	Marker,
	CircleMarker,
	Circle,
	Popup,
	Tooltip,
	Polyline,
	LayersControl,
	ScaleControl,
	useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import vesselcpa from '../../images/markerimages/cpapointmarker.png';
import aisDataIcon from '../../images/markerimages/aisData.png';
import pids from '../../images/Refinery1.png';
import chImg from '../../images/ch.png';
// import wbImg from '../../images/wb.png';
import kmpImg from '../../images/kmp.png';
// import tlpImg from '../../images/tlp.png';
// import tpImg from '../../images/tp.png';

import arabiclan from '../../arabic.json';
import englan from '../../english.json';
import frenchlan from '../../french.json';
import germanlan from '../../german.json';
import hindilan from '../../hindi.json';
import malayalamlan from '../../malayalam.json';
import { datetimeCovertertolocal } from '../datetime-converter';

const { Overlay } = LayersControl;

// ============================================================
// FIX DEFAULT MARKER ICON PATHS
// ============================================================
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
	iconRetinaUrl:
		'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
	iconUrl:
		'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
	shadowUrl:
		'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// ============================================================
// COORDINATE (interrogator) PIN ICON
// ============================================================
const COORDINATE_PIN_COLOR = '#eedb84';

const coordinateIcon = L.divIcon({
	className: '',
	html: `
		<svg width="20" height="32" viewBox="0 0 30 42" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 27 15 27s15-16.5 15-27C30 6.7 23.3 0 15 0z"
				fill="${COORDINATE_PIN_COLOR}"
				stroke="#000000"
				stroke-width="2"
			/>
			<circle cx="15" cy="15" r="6" fill="#000000" />
		</svg>
	`,
	iconSize: [30, 42],
	iconAnchor: [10, 30],
	popupAnchor: [0, -38],
});

// ============================================================
// ROU MARKER ICONS (KMP / WB / TP / TLP / CH)
// ============================================================
const CHicon = L.icon({
	iconUrl: chImg,
	iconSize: [30, 40],
	iconAnchor: [18, 36],
});

const KMPicon = L.icon({
	iconUrl: kmpImg,
	iconSize: [30, 40],
	iconAnchor: [18, 36],
});

// const WBicon = L.icon({ iconUrl: wbImg, iconSize: [30, 40], iconAnchor: [18, 36] });
// const TLPicon = L.icon({ iconUrl: tlpImg, iconSize: [30, 40], iconAnchor: [18, 36] });
// const TPicon = L.icon({ iconUrl: tpImg, iconSize: [30, 40], iconAnchor: [18, 36] });

// ============================================================
// LANGUAGE DATA (same pattern as GMap.jsx)
// ============================================================

const dashboardtype = process.env.REACT_APP_IDENTIFIER;
const permission = localStorage.getItem(`${dashboardtype.toUpperCase()}_PERMISSION`);

const langid =
	dashboardtype &&
	localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`) &&
	localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`)
		? parseInt(
			localStorage.getItem(
				`${dashboardtype.toUpperCase()}_LANGUAGE_ID`
			)
		)
		: 2;

const langdata =
	langid && langid == 1
		? arabiclan
		: langid == 2
			? englan
			: langid == 3
				? frenchlan
				: langid == 4
					? germanlan
					: langid == 5
						? hindilan
						: langid == 6
							? malayalamlan
							: englan;
const oneviewType = process.env.REACT_APP_ONEVIEW_TYPE ? process.env.REACT_APP_ONEVIEW_TYPE : 'pipeline';
const oneviewtypeLabeluper = oneviewType && oneviewType.toLowerCase() == 'pipeline' ? langdata && langdata.PIPELINE : oneviewType.toLowerCase() == 'airport' ? langdata && langdata.AIRPORT : oneviewType.toLowerCase() == 'border' ? langdata && langdata.BORDER : oneviewType.toLowerCase() == 'railway' ? langdata && langdata.RAILWAY : oneviewType.toLowerCase() == 'subsea' ? langdata && langdata.SUBSEA : 'PIPELINE';

// ============================================================
// COLORS
// ============================================================

const TRACK_COLORS = [
	'#2563eb',
	'#dc2626',
	'#16a34a',
	'#d97706',
	'#9333ea'
];

const ALGORITHM_COLORS = [
	'#FF0000',
	'#0000FF',
	'#00AA00',
	'#FF9900',
	'#9900FF',
	'#00AAAA',
	'#FF00AA',
	'#795548',
	'#607D8B',
	'#8BC34A'
];

const colorForAlgorithmId = (id) => {
	if (id === undefined || id === null || id === '') return null;
	const str = String(id);
	let hash = 0;
	for (let i = 0; i < str.length; i += 1) {
		hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
	}
	return ALGORITHM_COLORS[hash % ALGORITHM_COLORS.length];
};

const pipecolor = { color: '#000000' };
const odmetercolor = { color: '#036CD6' };
const bordercolor = { color: '#fc8383' };
const optionalcolor = { color: 'orange' };

// ============================================================
// AIS LIVE VESSEL TRACKING (ais.openwaters.io, with fallback)
// (unchanged — see original notes: anonymous requests must stay
// under 100 sq° bbox; this layer never moves the map camera.)
// ============================================================

const OPENWATERS_VESSELS_URL = 'https://ais.openwaters.io/v1/vessels';
const AIS_RESUBSCRIBE_DEBOUNCE_MS = 1200;
const AIS_MAX_ANONYMOUS_AREA_DEG2 = 100;

const AIS_FALLBACK_BBOX = [58, 4, 62, 11]; // south, west, north, east

const bboxAreaDeg2 = ([south, west, north, east]) =>
	Math.max(0, north - south) * Math.max(0, east - west);

const clampBboxToAreaCap = (bbox, maxAreaDeg2 = AIS_MAX_ANONYMOUS_AREA_DEG2) => {
	if (!bbox || bbox.length !== 4) return bbox;

	const [south, west, north, east] = bbox;
	const area = bboxAreaDeg2(bbox);
	if (area <= maxAreaDeg2 || area === 0) return bbox;

	const scale = Math.sqrt(maxAreaDeg2 / area) * 0.98;
	const latSpan = (north - south) * scale;
	const lonSpan = (east - west) * scale;
	const centerLat = (north + south) / 2;
	const centerLon = (east + west) / 2;

	return [
		centerLat - latSpan / 2,
		centerLon - lonSpan / 2,
		centerLat + latSpan / 2,
		centerLon + lonSpan / 2,
	];
};

const aisVesselIcon = (rotationDeg,) => {
	const rotation = Number.isFinite(rotationDeg) ? rotationDeg : 0;
	return L.divIcon({
		className: '',
		html: `
			<div style="width:22px;height:22px;transform:rotate(${rotation}deg);transform-origin:50% 50%;">
				<svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
					<polygon points="11,1 4,20 11,15.5 18,20" fill="#fccb2c" stroke="#000000" stroke-width="1" stroke-linejoin="round"/>
				</svg>
			</div>
		`,
		iconSize: [22, 22],
		iconAnchor: [11, 11],
		popupAnchor: [0, -10],
	});
};

const boundsToBboxParam = (bounds) => {
	if (!bounds) return null;
	const raw = [
		bounds.getSouth(),
		bounds.getWest(),
		bounds.getNorth(),
		bounds.getEast(),
	];
	return clampBboxToAreaCap(raw).join(',');
};

const parseOpenwatersFeatures = (geojson, includeKinds) => {
	const features = Array.isArray(geojson && geojson.features)
		? geojson.features
		: [];

	return features
		.filter((feature) => {
			const kind = feature && feature.properties
				? feature.properties.kind
				: undefined;
			return (
				!includeKinds ||
				includeKinds.length === 0 ||
				includeKinds.includes(kind)
			);
		})
		.map((feature) => {
			const coords =
				(feature.geometry && feature.geometry.coordinates) || [];
			const lng = coords[0];
			const lat = coords[1];
			const properties = feature.properties || {};

			const mmsi = properties.mmsi ?? feature.id ?? null;
			const kind = properties.kind ?? null;
			const name =
				typeof properties.name === 'string' &&
				properties.name.trim() ? properties.name.trim() : null;
			const cog = Number.isFinite(Number(properties.cog)) ? Number(properties.cog) : null;
			const sog = Number.isFinite(Number(properties.sog)) ? Number(properties.sog) : null;
			const heading = Number.isFinite(Number(properties.heading)) ? Number(properties.heading) : null;
			const navStatus = properties.nav_status ?? null;
			const seen = properties.seen ?? null;
			const source = properties.source ?? null;

			return {
				mmsi,
				kind,
				name,
				cog,
				sog,
				heading,
				navStatus,
				seen,
				source,
				lat: Number(lat),
				lng: Number(lng),
			};
		})
		.filter(
			(vessel) =>
				Number.isFinite(vessel.lat) && Number.isFinite(vessel.lng)
		);
};

const fetchOpenwatersBbox = (vesselsUrl, bboxParam, includeKinds, signal) => {
	const url = `${vesselsUrl}?bbox=${encodeURIComponent(bboxParam)}`;
	return fetch(url, { signal })
		.then((res) => {
			if (!res.ok) throw new Error(`AIS request failed: ${res.status}`);
			return res.json();
		})
		.then((geojson) => parseOpenwatersFeatures(geojson, includeKinds));
};

const fetchAisstreamSnapshot = (
	apiKey,
	bboxSouthWestNorthEast,
	{ timeoutMs = 6000, maxVessels = 200 } = {}
) => {
	return new Promise((resolve) => {
		if (!apiKey || typeof WebSocket === 'undefined') {
			resolve([]);
			return;
		}

		const [south, west, north, east] = bboxSouthWestNorthEast;
		let settled = false;
		let socket;
		const collected = new Map();

		const finish = () => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			try {
				socket && socket.close();
			} catch (err) {
				// noop
			}
			resolve(Array.from(collected.values()));
		};

		const timer = setTimeout(finish, timeoutMs);

		try {
			socket = new WebSocket('wss://stream.aisstream.io/v0/stream');
		} catch (err) {
			clearTimeout(timer);
			resolve([]);
			return;
		}

		socket.onopen = () => {
			socket.send(JSON.stringify({
				APIKey: apiKey,
				BoundingBoxes: [[[south, west], [north, east]]],
				FilterMessageTypes: ['PositionReport'],
			}));
		};

		socket.onmessage = (event) => {
			try {
				const payload = JSON.parse(event.data);
				const meta = payload.MetaData || {};
				const report = payload.Message && payload.Message.PositionReport;

				const lat = Number(meta.latitude);
				const lng = Number(meta.longitude);
				if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

				const mmsi = meta.MMSI ?? null;
				if (mmsi === null) return;

				collected.set(mmsi, {
					mmsi,
					kind: 'vessel',
					name: typeof meta.ShipName === 'string' && meta.ShipName.trim()
						? meta.ShipName.trim() : null,
					cog: report && Number.isFinite(Number(report.Cog)) ? Number(report.Cog) : null,
					sog: report && Number.isFinite(Number(report.Sog)) ? Number(report.Sog) : null,
					heading: report && Number.isFinite(Number(report.TrueHeading)) ? Number(report.TrueHeading) : null,
					navStatus: report && report.NavigationalStatus !== undefined ? report.NavigationalStatus : null,
					seen: meta.time_utc || null,
					source: 'aisstream.io',
					lat,
					lng,
				});

				if (collected.size >= maxVessels) finish();
			} catch (err) {
				// Ignore malformed frames.
			}
		};

		socket.onerror = finish;
		socket.onclose = finish;
	});
};

const AisVesselsLayer = ({
	vesselsUrl,
	pollIntervalMs,
	fixedBbox,
	includeKinds,
	maxVessels,
	fallbackBbox,
	aisstreamApiKey,
}) => {
	const map = useMap();

	const [vessels, setVessels] = useState([]);

	const fetchAbortRef = useRef(null);
	const pollTimerRef = useRef(null);
	const debounceTimerRef = useRef(null);
	const mountedRef = useRef(true);

	const clampedFallbackBbox = useMemo(
		() => (fallbackBbox ? clampBboxToAreaCap(fallbackBbox) : undefined),
		[fallbackBbox]
	);

	const applyLimit = useCallback((list) => (
		maxVessels > 0 && list.length > maxVessels
			? list.slice(0, maxVessels)
			: list
	), [maxVessels]);

	const fetchVessels = useCallback(() => {
		if (fetchAbortRef.current) fetchAbortRef.current.abort();
		const controller = new AbortController();
		fetchAbortRef.current = controller;

		const bboxParam =
			fixedBbox && fixedBbox.length === 4
				? clampBboxToAreaCap(fixedBbox).join(',')
				: boundsToBboxParam(map.getBounds());

		if (!bboxParam) return;

		fetchOpenwatersBbox(vesselsUrl, bboxParam, includeKinds, controller.signal)
			.then((localVessels) => {
				if (!mountedRef.current || controller.signal.aborted) return;

				if (localVessels.length > 0 || !clampedFallbackBbox) {
					setVessels(applyLimit(localVessels));
					return;
				}

				return fetchOpenwatersBbox(
					vesselsUrl,
					clampedFallbackBbox.join(','),
					includeKinds,
					controller.signal
				).then((fallbackVessels) => {
					if (!mountedRef.current || controller.signal.aborted) return;

					if (fallbackVessels.length > 0) {
						setVessels(applyLimit(fallbackVessels));
						return;
					}

					return fetchAisstreamSnapshot(aisstreamApiKey, clampedFallbackBbox)
						.then((streamVessels) => {
							if (!mountedRef.current || controller.signal.aborted) return;
							setVessels(applyLimit(streamVessels));
						});
				});
			})
			.catch((err) => {
				if (err && err.name !== 'AbortError') {
					// Swallow — supplementary layer.
				}
			});
	}, [map, vesselsUrl, fixedBbox, includeKinds, clampedFallbackBbox, aisstreamApiKey, applyLimit]);

	useEffect(() => {
		mountedRef.current = true;
		fetchVessels();

		pollTimerRef.current = setInterval(fetchVessels, pollIntervalMs);

		return () => {
			mountedRef.current = false;
			clearInterval(pollTimerRef.current);
			if (fetchAbortRef.current) fetchAbortRef.current.abort();
		};
	}, [vesselsUrl, pollIntervalMs]);

	useEffect(() => {
		if (fixedBbox && fixedBbox.length === 4) return undefined;

		const handleMoveEnd = () => {
			if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
			debounceTimerRef.current = setTimeout(
				fetchVessels,
				AIS_RESUBSCRIBE_DEBOUNCE_MS
			);
		};

		map.on('moveend zoomend', handleMoveEnd);
		return () => {
			map.off('moveend zoomend', handleMoveEnd);
			if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
		};
	}, [map, fixedBbox, fetchVessels]);

	return (
		<>
			{vessels.map((vessel) => {
				const rotation = Number.isFinite(vessel.heading)
					? vessel.heading
					: vessel.cog;
				const newTime = datetimeCovertertolocal(vessel.seen);

				return (
					<Marker
						key={`ais-${vessel.mmsi}`}
						position={[vessel.lat, vessel.lng]}
						icon={aisVesselIcon(rotation, '#1565c0')}
					>
						<Tooltip direction="top" offset={[0, -10]}>
							<strong>{vessel.name || `MMSI ${vessel.mmsi}`}</strong>
							<br />

							{(langdata && langdata.Latitude) || 'Latitude'}:
							{' '}
							{vessel.lat ?? 'No Data'}
							<br />
							{(langdata && langdata.Longitude) || 'Longitude'}:
							{' '}
							{vessel.lng ?? 'No Data'}
							<br />
						</Tooltip>

						<Popup>
							<strong>
								{vessel.name || ((langdata && langdata.Vessel) || 'Vessel')}
							</strong>
							<br />
							MMSI: {vessel.mmsi}
							<br />
							{(langdata && langdata.Latitude) || 'Latitude'}:
							{' '}
							{vessel.lat ?? 'No Data'}
							<br />
							{(langdata && langdata.Longitude) || 'Longitude'}:
							{' '}
							{vessel.lng ?? 'No Data'}
							<br />
							{(langdata && langdata.Speed) || 'Speed'}:
							{' '}
							{vessel.sog ?? 'No Data'} kn
							<br />
							{(langdata && langdata.Course) || 'Course'}:
							{' '}
							{vessel.cog ?? 'No Data'}°
							{vessel.seen && (
								<>
									<br />
									{(langdata && langdata.dateTime ? langdata.dateTime : 'Date Time')} : {(newTime.localdate + ' ' + newTime.localtime)}
								</>
							)}
							{vessel.source && (
								<>
									<br />
									{(langdata && langdata.Source) || 'Source'}: {vessel.source}
								</>
							)}
						</Popup>
					</Marker>
				);
			})}
		</>
	);
};

AisVesselsLayer.propTypes = {
	vesselsUrl: PropTypes.string.isRequired,
	pollIntervalMs: PropTypes.number,
	fixedBbox: PropTypes.arrayOf(PropTypes.number),
	includeKinds: PropTypes.arrayOf(PropTypes.string),
	maxVessels: PropTypes.number,
	fallbackBbox: PropTypes.arrayOf(PropTypes.number),
	aisstreamApiKey: PropTypes.string,
};

AisVesselsLayer.defaultProps = {
	pollIntervalMs: 15000,
	fixedBbox: undefined,
	includeKinds: ['vessel'],
	maxVessels: 500,
	fallbackBbox: undefined,
	aisstreamApiKey: undefined,
};

const vesselCpaIcon = L.icon({
	iconUrl: vesselcpa,
	iconSize: [28, 45],
	iconAnchor: [14, 45],
	popupAnchor: [0, -40],
});
const aisDataVesselIcon = L.icon({
	iconUrl: aisDataIcon,
	iconSize: [28, 45],
	iconAnchor: [14, 45],
	popupAnchor: [0, -40],
});

export { vesselCpaIcon, aisDataVesselIcon };

const pidsIcon = L.icon({
	iconUrl: pids,
	iconSize: [32, 32],
	iconAnchor: [16, 32],
	popupAnchor: [0, -28],
});

const labelIcon = (label, color) =>
	L.divIcon({
		html: `
			<div style="
				display:flex;
				align-items:center;
				justify-content:center;
				width:18px;
				height:18px;
				border-radius:50%;
				background:${color};
				color:#ffffff;
				font-size:10px;
				font-weight:bold;
				border:2px solid #ffffff;
				box-shadow:0 0 2px rgba(0,0,0,0.5);
			">
				${label}
			</div>
		`,
		className: '',
		iconSize: [18, 18],
		iconAnchor: [9, 9],
	});

// PEAK COORDINATES ICON (kept as a DOM divIcon for the standalone
// FibreCoordinateMarker; the bulk detection layer draws the same
// shape directly on canvas — see 'diamond' in CanvasPointsLayer).
const peakCoordinateIcon = L.divIcon({
	className: '',
	html: `
		<div style="
			width: 10px;
			height: 10px;
			background: #f75a00;
			border: 2px solid #f75a00;
			outline: 1px solid #000000;
			transform: rotate(110deg);
			box-shadow: 0 1px 4px rgba(0,0,0,0.7);
			position: relative;
			display: flex;
			align-items: center;
			justify-content: center;
		">
			<span style="
				color: #000000;
				font-weight: bold;
				font-size: 13px;
				line-height: 1;
				transform: rotate(-90deg);
			"></span>
		</div>
	`,
	iconSize: [18, 18],
	iconAnchor: [6, 6],
	popupAnchor: [0, -12],
});

const harbourIcon = L.divIcon({
	className: '',
	html: `
		<svg width="28" height="28" viewBox="0 0 34 34" xmlns="http://www.w3.org/2000/svg">
			<circle cx="17" cy="17" r="14.5" fill="none" stroke="#9c1263" stroke-width="3"/>
			<g transform="translate(17,17)" stroke="#9c1263" stroke-width="2.2" fill="none" stroke-linecap="round" stroke-linejoin="round">
				<circle cx="0" cy="-5.95" r="2.55"/>
				<line x1="0" y1="-3.4" x2="0" y2="8.5"/>
				<line x1="-3.4" y1="-2" x2="3.4" y2="-2"/>
				<path d="M -5.95 0 H -8.5 A 8.5 8.5 0 0 0 8.5 0 H 5.95"/>
			</g>
		</svg>
	`,
	iconSize: [34, 34],
	iconAnchor: [17, 17],
	popupAnchor: [0, -16],
});

const EXPAND_ICON = `
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
		<path d="M8 3H5a2 2 0 0 0-2 2v3"/>
		<path d="M21 8V5a2 2 0 0 0-2-2h-3"/>
		<path d="M3 16v3a2 2 0 0 0 2 2h3"/>
		<path d="M16 21h3a2 2 0 0 0 2-2v-3"/>
	</svg>
`;

const COMPRESS_ICON = `
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
		<path d="M8 3v3a2 2 0 0 1-2 2H3"/>
		<path d="M21 8h-3a2 2 0 0 1-2-2V3"/>
		<path d="M3 16h3a2 2 0 0 1 2 2v3"/>
		<path d="M16 21v-3a2 2 0 0 1 2-2h3"/>
	</svg>
`;

const FullscreenControl = ({ position, fullscreenTargetRef }) => {
	const map = useMap();

	useEffect(() => {
		const container =
			(fullscreenTargetRef && fullscreenTargetRef.current) ||
			map.getContainer();

		const titleEnter = (langdata && langdata.FullScreen) ? langdata.FullScreen : 'Full Screen';
		const titleExit = (langdata && langdata.ExitFullScreen) ? langdata.ExitFullScreen : 'Exit Full Screen';

		const FullscreenLeafletControl = L.Control.extend({
			options: { position: position || 'topleft' },

			onAdd() {
				const btn = L.DomUtil.create('a', 'leaflet-control-fullscreen-btn leaflet-bar-part');
				btn.href = '#';
				btn.title = titleEnter;
				btn.setAttribute('role', 'button');
				btn.style.cssText =
					'display:flex;align-items:center;justify-content:center;width:30px;height:30px;' +
					'background:#ffffff;text-decoration:none;' + 'cursor:pointer;';
				btn.innerHTML = EXPAND_ICON;

				L.DomEvent.disableClickPropagation(btn);
				L.DomEvent.on(btn, 'click', L.DomEvent.stop);
				L.DomEvent.on(btn, 'click', () => {
					if (!document.fullscreenElement) {
						container.requestFullscreen?.();
					} else {
						document.exitFullscreen?.();
					}
				});

				this._btn = btn;
				return btn;
			},

			onRemove() {
				if (this._btn) {
					L.DomEvent.off(this._btn);
				}
			},
		});

		const control = new FullscreenLeafletControl();
		map.addControl(control);

		const handleFullscreenChange = () => {
			const isFs = document.fullscreenElement === container;
			if (control._btn) {
				control._btn.title = isFs ? titleExit : titleEnter;
				control._btn.innerHTML = isFs ? COMPRESS_ICON : EXPAND_ICON;
			}
			setTimeout(() => map.invalidateSize(), 150);
		};

		document.addEventListener('fullscreenchange', handleFullscreenChange);

		return () => {
			document.removeEventListener('fullscreenchange', handleFullscreenChange);
			map.removeControl(control);
		};
	}, [map, position, fullscreenTargetRef]);

	return null;
};

FullscreenControl.propTypes = {
	position: PropTypes.oneOf(['topleft', 'topright', 'bottomleft', 'bottomright']),
	fullscreenTargetRef: PropTypes.object
};

FullscreenControl.defaultProps = {
	position: 'topleft',
	fullscreenTargetRef: null
};

const CoordinateGrid = () => {
	const map = useMap();
	const groupRef = useRef(null);

	useEffect(() => {
		const group = L.layerGroup().addTo(map);
		groupRef.current = group;

		const stepForZoom = (zoom) => {
			if (zoom >= 12) return 0.05;
			if (zoom >= 10) return 0.1;
			if (zoom >= 8) return 0.5;
			if (zoom >= 6) return 1;
			if (zoom >= 4) return 2;
			return 5;
		};

		const draw = () => {
			group.clearLayers();

			const bounds = map.getBounds();
			const zoom = map.getZoom();
			const step = stepForZoom(zoom);

			const south = bounds.getSouth();
			const north = bounds.getNorth();
			const west = bounds.getWest();
			const east = bounds.getEast();

			const startLat = Math.floor(south / step) * step;
			const startLng = Math.floor(west / step) * step;

			const lineOptions = {
				color: '#333333',
				weight: 0.6,
				opacity: 0.55,
				interactive: false,
				dashArray: '2,4',
			};

			const labelStyle =
				'background:transparent;color:#333;font-size:10px;font-weight:600;' +
				'text-shadow:0 0 3px #fff,0 0 3px #fff,0 0 3px #fff;white-space:nowrap;';

			for (let lat = startLat; lat <= north; lat += step) {
				group.addLayer(L.polyline([[lat, west], [lat, east]], lineOptions));
				group.addLayer(
					L.marker([lat, west], {
						interactive: false,
						icon: L.divIcon({
							className: '',
							html: `<div style="${labelStyle}">${lat.toFixed(2)}°</div>`,
							iconSize: [0, 0],
							iconAnchor: [-4, 6],
						}),
					})
				);
			}

			for (let lng = startLng; lng <= east; lng += step) {
				group.addLayer(L.polyline([[south, lng], [north, lng]], lineOptions));
				group.addLayer(
					L.marker([south, lng], {
						interactive: false,
						icon: L.divIcon({
							className: '',
							html: `<div style="${labelStyle}">${lng.toFixed(2)}°</div>`,
							iconSize: [0, 0],
							iconAnchor: [-4, -14],
						}),
					})
				);
			}
		};

		draw();
		map.on('moveend zoomend', draw);

		return () => {
			map.off('moveend zoomend', draw);
			map.removeLayer(group);
		};
	}, [map]);

	return null;
};

const HarbourLayer = () => {
	const map = useMap();
	const scriptRef = useRef(null);
	const requestIdRef = useRef(0);

	useEffect(() => {
		const group = L.layerGroup().addTo(map);

		const removeScript = () => {
			if (scriptRef.current && scriptRef.current.parentNode) {
				scriptRef.current.parentNode.removeChild(scriptRef.current);
			}
			scriptRef.current = null;
		};

		const renderHarbours = (harbours) => {
			group.clearLayers();

			harbours.forEach((harbour) => {
				if (!Number.isFinite(harbour.lat) || !Number.isFinite(harbour.lon)) {
					return;
				}

				const displayName =
					(harbour.name && harbour.name.replace(/_/g, ' ')) ||
					((langdata && langdata.Harbour) || 'Harbour');

				const marker = L.marker([harbour.lat, harbour.lon], { icon: harbourIcon });

				marker.bindPopup(
					`<strong>${displayName}</strong>` +
					(harbour.url
						? `<br/><a href="${harbour.url}" target="_blank" rel="noreferrer">${(langdata && langdata.MoreInfo) ? langdata.MoreInfo : 'More info'}</a>`
						: '')
				);

				group.addLayer(marker);
			});
		};

		const loadFromOverpass = (bounds, requestId) => {
			const bbox = [
				bounds.getSouth(),
				bounds.getWest(),
				bounds.getNorth(),
				bounds.getEast(),
			].join(',');

			const query =
				'[out:json][timeout:25];(' +
				`node["seamark:type"="harbour"](${bbox});` +
				`node["seamark:type"="harbour_basin"](${bbox});` +
				`node["leisure"="marina"](${bbox});` +
				`node["harbour"="yes"](${bbox});` +
				');out body;';

			fetch('https://overpass-api.de/api/interpreter', {
				method: 'POST',
				body: query,
			})
				.then((res) => res.json())
				.then((data) => {
					if (requestId !== requestIdRef.current) return;

					const harbours = (data.elements || []).map((element) => ({
						lat: element.lat,
						lon: element.lon,
						name:
							(element.tags &&
								(element.tags.name || element.tags['seamark:name'])) ||
							null,
						url: null,
					}));

					renderHarbours(harbours);
				})
				.catch(() => {
					// Fail quietly, supplementary layer.
				});
		};

		const load = () => {
			const requestId = ++requestIdRef.current;
			const bounds = map.getBounds();
			const zoom = Math.round(map.getZoom());

			const collected = [];

			window.putHarbourMarker = (id, lon, lat, name, url) => {
				if (requestId !== requestIdRef.current) return;
				collected.push({ id, lon: parseFloat(lon), lat: parseFloat(lat), name, url });
			};

			removeScript();

			const params = new URLSearchParams({
				b: bounds.getSouth(),
				t: bounds.getNorth(),
				l: bounds.getWest(),
				r: bounds.getEast(),
				ucid: '0',
				maxSize: '5',
				zoom: String(zoom),
			});

			const script = document.createElement('script');
			script.src = `https://harbour.openseamap.org/getHarbours.php?${params.toString()}`;
			script.async = true;

			let settled = false;

			const finishPrimary = () => {
				if (settled || requestId !== requestIdRef.current) return;
				settled = true;

				if (collected.length > 0) {
					renderHarbours(collected);
				} else if (zoom >= 9) {
					loadFromOverpass(bounds, requestId);
				} else {
					group.clearLayers();
				}
			};

			script.onload = finishPrimary;
			script.onerror = () => {
				if (requestId !== requestIdRef.current) return;
				settled = true;
				if (zoom >= 9) {
					loadFromOverpass(bounds, requestId);
				} else {
					group.clearLayers();
				}
			};

			scriptRef.current = script;
			document.body.appendChild(script);

			setTimeout(finishPrimary, 4000);
		};

		load();
		map.on('moveend zoomend', load);

		return () => {
			requestIdRef.current += 1;
			map.off('moveend zoomend', load);
			map.removeLayer(group);
			removeScript();
			delete window.putHarbourMarker;
		};
	}, [map]);

	return null;
};

const CompassRoseControl = () => {
	const map = useMap();

	useEffect(() => {
		const CompassRoseLeafletControl = L.Control.extend({
			options: { position: 'bottomleft' },

			onAdd() {
				const div = L.DomUtil.create('div', 'compass-rose-control');
				div.style.cssText = 'pointer-events:none;';
				div.innerHTML = `
					<svg viewBox="0 0 100 100" width="64" height="64">
						<circle cx="50" cy="50" r="46" fill="rgba(255,255,255,0.85)" stroke="#333" stroke-width="1.5" />
						<polygon points="50,8 56,50 50,42 44,50" fill="#d32f2f" />
						<polygon points="50,92 56,50 50,58 44,50" fill="#333333" />
						<text x="50" y="20" font-size="11" text-anchor="middle" fill="#333">N</text>
						<text x="50" y="87" font-size="10" text-anchor="middle" fill="#333">S</text>
						<text x="15" y="54" font-size="10" text-anchor="middle" fill="#333">W</text>
						<text x="85" y="54" font-size="10" text-anchor="middle" fill="#333">E</text>
					</svg>
				`;
				return div;
			},
		});

		const control = new CompassRoseLeafletControl();
		map.addControl(control);

		return () => {
			map.removeControl(control);
		};
	}, [map]);

	return null;
};

const MapReadyBridge = ({ onReady }) => {
	const map = useMap();
	useEffect(() => {
		onReady(map);
	}, [map, onReady]);
	return null;
};

MapReadyBridge.propTypes = { onReady: PropTypes.func.isRequired };

const formatLatLon = (lat, lng) => {
	const ns = lat >= 0 ? 'N' : 'S';
	const we = lng >= 0 ? 'E' : 'W';

	const latAbsMin = Math.abs(lat) * 60;
	const lngAbsMin = Math.abs(lng) * 60;

	const latDeg = Math.floor(latAbsMin / 60);
	const lngDeg = Math.floor(lngAbsMin / 60);

	const latMin = (latAbsMin - latDeg * 60).toFixed(3).padStart(6, '0');
	const lngMin = (lngAbsMin - lngDeg * 60).toFixed(3).padStart(6, '0');

	return `${ns}${latDeg}\u00B0${latMin}' ${we}${lngDeg}\u00B0${lngMin}'`;
};

const MapPositionBridge = ({ targetRef }) => {
	const map = useMap();

	useEffect(() => {
		const update = (latlng) => {
			if (!targetRef.current || !latlng) return;
			const zoom = map.getZoom();
			const lat = latlng.lat ?? 0;
			const lng = latlng.lng ?? 0;
			targetRef.current.textContent = `Zoom:${Math.round(zoom)} ${formatLatLon(lat, lng)}`;
		};

		const handleMouseMove = (e) => e?.latlng && update(e.latlng);
		const handleMoveEnd = () => {
			const center = map.getCenter();
			if (center) update(center);
		};

		map.on('mousemove', handleMouseMove);
		map.on('moveend zoomend', handleMoveEnd);

		if (map.getCenter()) {
			update(map.getCenter());
		}

		return () => {
			map.off('mousemove', handleMouseMove);
			map.off('moveend zoomend', handleMoveEnd);
		};
	}, [map, targetRef]);

	return null;
};

MapPositionBridge.propTypes = { targetRef: PropTypes.object.isRequired };

const buildPermalink = (position) => {
	if (typeof window === 'undefined' || !position) return '';
	try {
		const url = new URL(window.location.href);
		url.hash = `map=${Math.round(position.zoom)}/${position.lat.toFixed(5)}/${position.lng.toFixed(5)}`;
		return url.toString();
	} catch (err) {
		return '';
	}
};

const toolbarStyles = {
	wrapper: {
		position: 'absolute',
		top: 8,
		left: 54,
		zIndex: 1000,
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		width: 'fit-content',
		background: '#e9edf1',
		border: '1px solid #b9c2cc',
		borderRadius: 4,
		boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
		fontFamily: 'Tahoma, Verdana, Arial, sans-serif',
		fontSize: 12,
		color: '#222',
		padding: '3px 6px',
	},
	logo: { fontSize: 15, padding: '0 4px' },
	searchWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
	searchInput: {
		width: 190,
		height: 22,
		border: '1px solid #9aa4af',
		borderRadius: 2,
		padding: '0 6px',
		fontSize: 12,
	},
	searchBtn: {
		height: 24,
		border: '1px solid #9aa4af',
		borderLeft: 'none',
		background: '#f4f6f8',
		cursor: 'pointer',
	},
	searchDropdown: {
		position: 'absolute',
		top: 26,
		left: 0,
		width: 300,
		maxHeight: 220,
		overflowY: 'auto',
		background: '#fff',
		border: '1px solid #9aa4af',
		boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
		zIndex: 1001,
	},
	searchResultRow: {
		padding: '6px 8px',
		fontSize: 12,
		borderBottom: '1px solid #eee',
		cursor: 'pointer',
	},
	menuItem: {
		padding: '0 8px',
		height: 26,
		display: 'flex',
		alignItems: 'center',
		gap: 4,
		cursor: 'pointer',
		userSelect: 'none',
		whiteSpace: 'nowrap',
		borderRadius: 3,
	},
	menuItemWrap: { position: 'relative' },
	dropdown: {
		position: 'absolute',
		top: 28,
		left: 0,
		minWidth: 200,
		background: '#fff',
		border: '1px solid #9aa4af',
		boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
		zIndex: 1001,
		padding: '4px 0',
	},
	dropdownRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		padding: '5px 10px',
		fontSize: 12,
		cursor: 'pointer',
	},
	dropdownDivider: { borderTop: '1px solid #e2e6ea', margin: '4px 0' },
};

const bottomBarStyles = {
	wrapper: {
		position: 'absolute',
		left: 0,
		right: 0,
		bottom: 0,
		height: 22,
		zIndex: 1000,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		background: 'rgba(233,237,241,0.9)',
		borderTop: '1px solid #b9c2cc',
		fontFamily: 'Tahoma, Verdana, Arial, sans-serif',
		fontSize: 11,
		color: '#333',
		padding: '0 8px',
	},
	attribution: { display: 'flex', gap: 4 },
	right: { display: 'flex', alignItems: 'center', gap: 12 },
	permalink: { color: '#0d6efd', cursor: 'pointer', textDecoration: 'underline' },
	position: { fontVariantNumeric: 'tabular-nums' },
};

const coordinatePopupStyles = {
	actions: {
		marginTop: 8,
		paddingTop: 8,
		borderTop: '1px solid #eee',
	},
	deleteBtn: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		border: '1px solid #d32f2f',
		color: '#d32f2f',
		background: '#fff',
		borderRadius: 3,
		padding: '3px 10px',
		fontSize: 12,
		cursor: 'pointer',
	},
	confirmText: {
		fontSize: 12,
		marginBottom: 6,
		color: '#333',
	},
	confirmBtn: {
		border: '1px solid #d32f2f',
		background: '#d32f2f',
		color: '#fff',
		borderRadius: 3,
		padding: '3px 10px',
		fontSize: 12,
		cursor: 'pointer',
		marginRight: 6,
	},
	cancelBtn: {
		border: '1px solid #9aa4af',
		background: '#fff',
		color: '#333',
		borderRadius: 3,
		padding: '3px 10px',
		fontSize: 12,
		cursor: 'pointer',
	},
};

const TopToolbar = ({ map, layerVisibility, onToggleLayer, showAisMenuItem }) => {
	const [viewOpen, setViewOpen] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [searchResults, setSearchResults] = useState([]);
	const containerRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (containerRef.current && !containerRef.current.contains(e.target)) {
				setViewOpen(false);
				setSearchResults([]);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const runSearch = async () => {
		if (!searchText.trim()) return;
		try {
			const res = await fetch(
				`https://nominatim.openstreetmap.org/search?format=json&limit=6&q=${encodeURIComponent(searchText)}`
			);
			const data = await res.json();
			setSearchResults(data || []);
		} catch (err) {
			setSearchResults([]);
		}
	};

	const jumpToResult = (result) => {
		if (!map) return;
		map.setView([parseFloat(result.lat), parseFloat(result.lon)], 14);
		setSearchResults([]);
		setSearchText(result.display_name);
	};

	const viewMenuItems = [
		{ key: 'seaMarks', label: (langdata && langdata.SeaMarks) || 'Sea Marks' },
		{ key: 'harbours', label: (langdata && langdata.Harbours) || 'Harbours' },
		{ key: 'aerialPhoto', label: (langdata && langdata.AerialPhoto) || 'Aerial photo' },
		{ key: 'coordinateGrid', label: (langdata && langdata.CoordinateGrid) || 'Coordinate Grid' },
		{ key: 'compassRose', label: (langdata && langdata.Compass) || 'Compass' },
		{ key: 'waterDepth', label: (langdata && langdata.WaterDepth) || 'Water depth' },
		{ key: 'depthContours', label: (langdata && langdata.DepthContours) || 'Depth contours ' },
		...(showAisMenuItem
			? [{ key: 'aisVessels', label: (langdata && langdata.AISVessels) || 'AIS Vessels' }]
			: []),
	];

	return (
		<div ref={containerRef} style={toolbarStyles.wrapper}>
			<div style={toolbarStyles.logo}>⚓</div>

			<div style={toolbarStyles.searchWrap}>
				<input
					style={toolbarStyles.searchInput}
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					onKeyDown={(e) => e.key === 'Enter' && runSearch()}
					placeholder={(langdata && langdata.Search) || 'Search...'}
				/>
				<button type="button" style={toolbarStyles.searchBtn} onClick={runSearch}>
					🔍
				</button>

				{searchResults.length > 0 && (
					<div style={toolbarStyles.searchDropdown}>
						{searchResults.map((result) => (
							<div
								key={result.place_id}
								style={toolbarStyles.searchResultRow}
								onClick={() => jumpToResult(result)}
							>
								{result.display_name}
							</div>
						))}
					</div>
				)}
			</div>

			<div style={toolbarStyles.menuItemWrap}>
				<div
					style={toolbarStyles.menuItem}
					onClick={() => setViewOpen((prev) => !prev)}
				>
					👁 {(langdata && langdata.View) || 'View'}
				</div>

				{viewOpen && (
					<div style={toolbarStyles.dropdown}>
						{viewMenuItems.map((item) => (
							<label key={item.key} style={toolbarStyles.dropdownRow}>
								<input
									type="checkbox"
									checked={!!layerVisibility[item.key]}
									onChange={() => onToggleLayer(item.key)}
								/>
								{item.label}
							</label>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

TopToolbar.propTypes = {
	map: PropTypes.object,
	layerVisibility: PropTypes.object.isRequired,
	onToggleLayer: PropTypes.func.isRequired,
	showAisMenuItem: PropTypes.bool,
};

TopToolbar.defaultProps = { map: null, showAisMenuItem: true };

const BottomInfoBar = ({ map, positionRef }) => {
	const [copied, setCopied] = useState(false);

	const copyPermalink = async () => {
		if (!map) return;
		const center = map.getCenter();
		const zoom = map.getZoom();
		const link = buildPermalink({ lat: center.lat, lng: center.lng, zoom });
		if (!link) return;
		try {
			await navigator.clipboard.writeText(link);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch (err) {
			// Clipboard access can fail in some contexts; fail silently.
		}
	};

	return (
		<div style={bottomBarStyles.wrapper}>
			<div style={bottomBarStyles.attribution}>
				<span>
					© <a href="https://openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors
				</span>
				<span>·</span>
				<span>
					© <a href="https://www.openseamap.org" target="_blank" rel="noreferrer">OpenSeaMap</a> contributors
				</span>
				<span>·</span>
				<span>
					AIS © <a href="https://openwaters.io" target="_blank" rel="noreferrer">Open Waters</a>
				</span>
			</div>

			<div style={bottomBarStyles.right}>
				<span style={bottomBarStyles.permalink} onClick={copyPermalink}>
					{copied ? ((langdata && langdata.Copied) || 'Copied!') : ((langdata && langdata.Permalink) || 'Permalink')}
				</span>

				<span ref={positionRef} style={bottomBarStyles.position} />
			</div>
		</div>
	);
};

BottomInfoBar.propTypes = {
	map: PropTypes.object,
	positionRef: PropTypes.object.isRequired,
};

BottomInfoBar.defaultProps = { map: null };

// ============================================================
// COORDINATE (interrogator) HOVER CONTENT + MARKER
// ============================================================

const CoordinateHoverContent = ({ coordinate }) => (
	<>
		{(langdata && langdata.Name ? langdata.Name : 'Name')}
		:
		{' '}
		{coordinate.name || 'No Data'}
		<br />

		{(langdata && langdata.channelName ? langdata.channelName : 'Channel Name')}
		:
		{' '}
		{coordinate.channelName || 'No Data'}
		<br />

		{(langdata && langdata.PidsName ? langdata.PidsName : 'Pids Name')}
		:
		{' '}
		{coordinate.pidsName || 'No Data'}
		<br />

		{(langdata && langdata.Latitude ? langdata.Latitude : 'Latitude')}
		:
		{' '}
		{coordinate.latitude}
		<br />

		{(langdata && langdata.Longitude ? langdata.Longitude : 'Longitude')}
		:
		{' '}
		{coordinate.longitude}
		<br />

		{(langdata && langdata.Distance ? langdata.Distance + ' (km)' : 'Distance (km)')}
		:
		{' '}
		{coordinate.distance || 'No Data'}
		<br />

		{(langdata && langdata.FibreDistance ? langdata.FibreDistance + ' (m)' : 'Fibre Distance (m)')}
		:
		{' '}
		{coordinate.ODMeter || 'No Data'}
		<br />

		{(langdata && langdata.DateTime ? langdata.DateTime : 'Date Time')}
		:
		{' '}
		{coordinate.createdAt || 'No Data'}
	</>
);

CoordinateHoverContent.propTypes = {
	coordinate: PropTypes.shape({
		name: PropTypes.string,
		channelName: PropTypes.string,
		pidsName: PropTypes.string,
		latitude: PropTypes.number,
		longitude: PropTypes.number,
		distance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		ODMeter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		createdAt: PropTypes.string,
	}).isRequired,
};

const CoordinateDeleteActions = ({ coordinate, onDelete }) => {
	const [confirming, setConfirming] = useState(false);
	const [deleting, setDeleting] = useState(false);

	if (!onDelete) {
		return null;
	}

	const handleDeleteClick = (e) => {
		e.stopPropagation();
		setConfirming(true);
	};

	const handleCancel = (e) => {
		e.stopPropagation();
		setConfirming(false);
	};

	const handleConfirm = async (e) => {
		e.stopPropagation();
		setDeleting(true);
		try {
			await onDelete(coordinate);
		} finally {
			setDeleting(false);
			setConfirming(false);
		}
	};

	return (
		<div style={coordinatePopupStyles.actions}>
			{!confirming ? (
				<button
					type="button"
					style={coordinatePopupStyles.deleteBtn}
					onClick={handleDeleteClick}
					disabled={deleting}
				>
					{(langdata && langdata.Delete) || 'Delete'}
				</button>
			) : (
				<>
					<div style={coordinatePopupStyles.confirmText}>
						{(langdata && langdata.ConfirmDeleteCoordinate) ||
							'Delete this coordinate?'}
					</div>
					<button
						type="button"
						style={coordinatePopupStyles.confirmBtn}
						onClick={handleConfirm}
						disabled={deleting}
					>
						{deleting
							? ((langdata && langdata.Deleting) || 'Deleting...')
							: ((langdata && langdata.YesDelete) || 'Yes, delete')}
					</button>
					<button
						type="button"
						style={coordinatePopupStyles.cancelBtn}
						onClick={handleCancel}
						disabled={deleting}
					>
						{(langdata && langdata.Cancel) || 'Cancel'}
					</button>
				</>
			)}
		</div>
	);
};

CoordinateDeleteActions.propTypes = {
	coordinate: PropTypes.object.isRequired,
	onDelete: PropTypes.func,
};

CoordinateDeleteActions.defaultProps = {
	onDelete: null,
};

const CoordinateMarker = ({ coordinate, onDelete }) => (
	<Marker
		position={[coordinate.latitude, coordinate.longitude]}
		icon={coordinateIcon}
	>
		<Tooltip direction="top" offset={[0, -36]}>
			<CoordinateHoverContent coordinate={coordinate} />
		</Tooltip>

		<Popup minWidth={220}>
			<strong>
				{coordinate.name || ((langdata && langdata.Coordinate) || 'Coordinate')}
			</strong>
			<br />
			<CoordinateHoverContent coordinate={coordinate} />
			<CoordinateDeleteActions coordinate={coordinate} onDelete={onDelete} />
		</Popup>
	</Marker>
);

CoordinateMarker.propTypes = {
	coordinate: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		name: PropTypes.string,
		latitude: PropTypes.number,
		longitude: PropTypes.number,
		channelName: PropTypes.string,
		pidsName: PropTypes.string,
		createdAt: PropTypes.string,
	}).isRequired,
	onDelete: PropTypes.func,
};

CoordinateMarker.defaultProps = {
	onDelete: null,
};

const FibreCoordinateMarker = ({ coordinate, }) => (
	<Marker
		position={[coordinate.fibrelatitude, coordinate.fibrelongitude]}
		icon={peakCoordinateIcon}
	>
		<Tooltip direction="top" offset={[0, -36]}>
			<CoordinateHoverContent coordinate={coordinate} />
		</Tooltip>

		<Popup minWidth={220}>
			<strong>
				{coordinate.name || ((langdata && langdata.Coordinate) || 'Coordinate')}
			</strong>
			<br />
			<CoordinateHoverContent coordinate={coordinate} />
		</Popup>
	</Marker>
);
FibreCoordinateMarker.propTypes = {
	coordinate: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		name: PropTypes.string,
		fibrelatitude: PropTypes.number,
		fibrelongitude: PropTypes.number,
		channelName: PropTypes.string,
		pidsName: PropTypes.string,
		createdAt: PropTypes.string,
	}).isRequired,
	onDelete: PropTypes.func,
};

FibreCoordinateMarker.defaultProps = {
	onDelete: null,
};

// ============================================================
// DETECTION HOVER CONTENT
// ============================================================
const DetectionHoverContent = ({ info, point }) => {

	const dateTime = datetimeCovertertolocal(info.startTimeHover);
	const newDateTime = `${dateTime.localdate} ${dateTime.localtime}`;

	const hasHoverLat = info && info.hoverLat !== undefined && info.hoverLat !== null;
	const hasHoverLng = info && info.hoverLng !== undefined && info.hoverLng !== null;

	const pointLat = hasHoverLat ? info.hoverLat : point.lat;

	const pointLng = hasHoverLng
		? info.hoverLng
		: (point.lng !== undefined ? point.lng : point.lon);

	// Safe distance rendering (previously `info?.cpaSourceDistance.toFixed`
	// threw when the value was null/undefined).
	const distanceValue = info ? info.cpaSourceDistance : null;

	return (
		<>
			{(langdata && langdata.EventType ? langdata.EventType : 'Event Type')}
			:
			{' '}
			{(info && info.eventType) || 'No Data'}
			<br />

			{(langdata && langdata.PidsName ? langdata.PidsName : 'PidsName')}
			:
			{' '}
			{(info && info.pidsName) ?? 'No Data'}
			{' '}
			<br />

			{['superadmin'].includes(permission) && (
				<>
					{(langdata && langdata.Algorithm
						? langdata.Algorithm
						: 'Algorithm')}
					:
					{' '}
					{(info && info.detectionAlgorithmTypeName) || 'No Data'}
					<br />
				</>
			)}

			{(langdata && langdata.Channel ? langdata.Channel : 'Channel')}
			:
			{' '}
			{(info && info.channelName) || 'No Data'}
			<br />

			{(langdata && langdata.Latitude ? langdata.Latitude : 'Latitude')}
			:
			{' '}
			{pointLat}
			<br />

			{(langdata && langdata.Longitude ? langdata.Longitude : 'Longitude')}
			:
			{' '}
			{pointLng}
			<br />

			{(langdata && langdata.DistanceToFibre
				? langdata.DistanceToFibre + '(m)'
				: 'Distance to fibre (m)')}
			:
			{' '}
			{Array.isArray(distanceValue)
				? distanceValue.map((distance, index) => (
					<div key={index}>
						{distance}
					</div>
				))
				: (distanceValue !== null && distanceValue !== undefined && Number.isFinite(Number(distanceValue))
					? Number(distanceValue).toFixed(3)
					: 'No Data')}
			<br />

			{(langdata && langdata.Speed ? langdata.Speed : 'Speed')}
			:
			{' '}
			{(info && info.sourceSpeedms ? info.sourceSpeedms.toFixed(3) + ' m/s' : 'No Data')}
			{' '}
			<br />
			{langdata.FibreDistance ? langdata?.FibreDistance + ' (m)' : 'Fiber Distance (m)'}
			:
			{' '}
			{(info && info.peakDistance) ?? 'No Data'}
			{' '}
			<br />
			{(langdata && langdata.StartTime ? langdata.StartTime : 'Start Time')}
			:
			{' '}
			{newDateTime ?? 'No Data'}
			<br />
		</>
	);
};

DetectionHoverContent.propTypes = {
	info: PropTypes.shape({
		eventType: PropTypes.string,
		detectionAlgorithmTypeName: PropTypes.string,
		channelName: PropTypes.string,
		pidsName: PropTypes.string,
		peakDistance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		sourceSpeedms: PropTypes.number,
		cpaSourceDistance: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
		startTimeHover: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		hoverLat: PropTypes.number,
		hoverLng: PropTypes.number,
	}),
	point: PropTypes.object.isRequired,
	moreInfo: PropTypes.shape({ pidsName: PropTypes.string, detectionAlgorithmTypeName: PropTypes.string, })
};

DetectionHoverContent.defaultProps = {
	info: null
};

// ============================================================
// ASN HOVER CONTENT
// ============================================================
const AsnHoverContent = ({ name, point }) => {
	const dt = point.datetime ? datetimeCovertertolocal(point.datetime) : null;
	const speedText =
		point.speed !== null && point.speed !== undefined && point.speed !== ''
			? `${point.speed}`
			: 'No Data';

	return (
		<>
			{(langdata && langdata.Name ? langdata.Name : 'Name')}
			:
			{' '}
			{name || 'No Data'}
			<br />

			{(langdata && langdata.Speed ? langdata.Speed + ' (m/s)' : 'Speed (m/s)')}
			:
			{' '}
			{speedText}
			<br />

			{(langdata && langdata.Latitude ? langdata.Latitude : 'Latitude')}
			:
			{' '}
			{point.lat}
			<br />

			{(langdata && langdata.Longitude ? langdata.Longitude : 'Longitude')}
			:
			{' '}
			{point.lng}
			<br />

			{(langdata && langdata.DateTime ? langdata.DateTime : 'Date Time')}
			:
			{' '}
			{dt ? `${dt.localdate} ${dt.localtime}` : 'No Data'}
		</>
	);
};

AsnHoverContent.propTypes = {
	name: PropTypes.string,
	point: PropTypes.shape({
		lat: PropTypes.number,
		lng: PropTypes.number,
		speed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		datetime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	}).isRequired,
};

AsnHoverContent.defaultProps = { name: '' };

// ============================================================
// PER-POINT VALUE HELPER
// ============================================================
const pickValueForIndex = (data, index) => {
	if (Array.isArray(data)) {
		return index >= 0 && index < data.length
			? data[index]
			: null;
	}
	return data ?? null;
};

// ============================================================
// PERFORMANCE: ARRAY-BASED SINGLE-CANVAS POINT LAYER
//
// Plotting tens of thousands of points as individual Leaflet layers
// (even canvas CircleMarkers) creates one object + one hit-test entry
// per point, which is what froze the UI. Instead, points are now passed
// to the map as ARRAYS ("series"):
//
//   { kind: 'points',  lats: [], lngs: [], radius, style,
//     tooltipAt(i), tooltipOptions }
//   { kind: 'markers', lats: [], lngs: [], icon | shape:'diamond',
//     tooltipAt(i), popupAt(i), tooltipOptions, popupOptions }
//
// CanvasPointsLayer converts each series to typed arrays ONCE and draws
// everything on ONE <canvas>:
//
//   • points are projected with plain Web-Mercator math (no per-point
//     LatLng/Point objects);
//   • only points inside the (padded) viewport are drawn;
//   • points that would land on the same few pixels are drawn once
//     (huge saving when zoomed out);
//   • every style group is drawn as ONE path (one fill + one stroke);
//   • the canvas is redrawn only on moveend / zoom end (panning and
//     zoom animation use a CSS transform, like Leaflet's own renderer);
//   • hover tooltips / click popups are resolved through a lazily built
//     pixel grid and rendered only for the single point under the
//     cursor (renderToStaticMarkup on demand).
//
// Marker icons (start / CPA / AIS images, peak diamond) are drawn on the
// same canvas, so there are no DOM marker nodes for detections at all.
// ============================================================
const BULK_PANE = 'bulkPointsPane';
const DEDUPE_CELL = 4;
const DEDUPE_ENABLED = false;
const HIT_CELL = 16;
const TWO_PI = Math.PI * 2;
const DIAMOND_ANGLE = (110 * Math.PI) / 180;
const MAX_MERC_LAT = 85.0511287798;

const spriteCache = new Map();
const loadSprite = (url, onLoad) => {
	let rec = spriteCache.get(url);
	if (!rec) {
		const img = new Image();
		rec = { img, loaded: false, callbacks: [] };
		img.onload = () => {
			rec.loaded = true;
			rec.callbacks.forEach((cb) => cb());
			rec.callbacks = [];
		};
		img.src = url;
		spriteCache.set(url, rec);
	}
	if (!rec.loaded && onLoad) rec.callbacks.push(onLoad);
	return rec;
};

const lazyHtml = (renderFn) => () => {
	try {
		return renderToStaticMarkup(renderFn());
	} catch (err) {
		return '';
	}
};

const CanvasPointsLayer = L.Layer.extend({
	options: { pane: BULK_PANE, padding: 0.5 },

	initialize(seriesList, options) {
		L.setOptions(this, options);
		this._series = [];
		this._groups = [];
		this._hitCells = 1;
		this._build(seriesList || []);
	},

	// ---------- data preparation (runs once per layer) ----------
	_build(list) {
		const groupMap = new Map();
		const rad = Math.PI / 180;

		list.forEach((s) => {
			const lats = s.lats || [];
			const lngs = s.lngs || [];
			const n = Math.min(lats.length, lngs.length);
			if (!n) return;

			const mx = new Float64Array(n);
			const my = new Float64Array(n);
			for (let i = 0; i < n; i += 1) {
				const lat = Number(lats[i]);
				const lng = Number(lngs[i]);
				if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
					mx[i] = NaN;
					my[i] = NaN;
				} else {
					const clamped = Math.max(Math.min(lat, MAX_MERC_LAT), -MAX_MERC_LAT);
					const sin = Math.sin(clamped * rad);
					mx[i] = (lng + 180) / 360;
					my[i] = 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
				}
			}

			let type;
			let key;
			let proto = {};

			if (s.kind === 'points') {
				const st = s.style || {};
				const r = s.radius || 5;
				type = 'circle';
				key = `c|${r}|${st.color}|${st.weight}|${st.fillColor}|${st.fillOpacity}|${st.opacity}`;
				proto = { r, style: st, box: [-r - 2, -r - 2, r + 2, r + 2] };
			} else {
				const iconOpts = s.icon && s.icon.options;
				const url = iconOpts && iconOpts.iconUrl;
				if (s.shape !== 'diamond' && url) {
					const size = iconOpts.iconSize || [28, 45];
					const anchor = iconOpts.iconAnchor || [size[0] / 2, size[1]];
					type = 'image';
					key = `i|${url}`;
					proto = {
						url,
						size,
						anchor,
						popupAnchor: iconOpts.popupAnchor || [0, 0],
						box: [-anchor[0], -anchor[1], size[0] - anchor[0], size[1] - anchor[1]],
					};
				} else {
					type = 'diamond';
					key = 'd';
					proto = { popupAnchor: [0, -12], box: [-8, -8, 8, 8] };
				}
			}

			let group = groupMap.get(key);
			if (!group) {
				group = { type, key, series: [], ...proto };
				groupMap.set(key, group);
			}

			const series = {
				mx,
				my,
				n,
				lats,
				lngs,
				tooltipAt: s.tooltipAt || null,
				popupAt: s.popupAt || null,
				tooltipOptions: s.tooltipOptions || {},
				popupOptions: s.popupOptions || {},
				hittable: !!(s.tooltipAt || s.popupAt),
				group,
			};
			group.series.push(series);
			this._series.push(series);
		});

		const order = { circle: 0, diamond: 1, image: 2 };
		this._groups = Array.from(groupMap.values()).sort((a, b) => order[a.type] - order[b.type]);

		let extent = 0;
		this._groups.forEach((g) => {
			extent = Math.max(extent, ...g.box.map((v) => Math.abs(v)));
		});
		this._hitCells = Math.max(1, Math.ceil(extent / HIT_CELL));
	},

	// ---------- Leaflet lifecycle ----------
	onAdd(map) {
		this._map = map;

		if (!map.getPane(BULK_PANE)) {
			const pane = map.createPane(BULK_PANE);
			pane.style.zIndex = 450;
			pane.style.pointerEvents = 'none';
		}

		const canvas = L.DomUtil.create('canvas', 'leaflet-bulk-points');
		if (map._zoomAnimated) L.DomUtil.addClass(canvas, 'leaflet-zoom-animated');
		canvas.style.pointerEvents = 'none';
		canvas.style.maxWidth = 'none';
		map.getPane(this.options.pane).appendChild(canvas);

		this._canvas = canvas;
		this._ctx = canvas.getContext('2d');
		this._hoverKey = null;
		this._update();
	},

	onRemove() {
		if (this._raf) cancelAnimationFrame(this._raf);
		if (this._hoverRaf) cancelAnimationFrame(this._hoverRaf);
		this._raf = null;
		this._hoverRaf = null;
		this._closeTip();
		if (this._canvas && this._canvas.parentNode) {
			this._canvas.parentNode.removeChild(this._canvas);
		}
		this._canvas = null;
		this._ctx = null;
		this._drawn = null;
		this._grid = null;
		this._map = null;
	},

	getEvents() {
		const events = {
			viewreset: this._schedule,
			moveend: this._schedule,
			zoom: this._onZoom,
			zoomstart: this._closeTip,
			mousemove: this._onMouseMove,
			mouseout: this._closeTip,
			click: this._onClick,
		};
		if (this._map && this._map._zoomAnimated) {
			events.zoomanim = this._onAnimZoom;
		}
		return events;
	},

	// ---------- redraw scheduling ----------
	_schedule() {
		if (this._raf || !this._map) return;
		this._raf = requestAnimationFrame(() => {
			this._raf = null;
			this._update();
		});
	},

	_onAnimZoom(e) {
		this._updateTransform(e.center, e.zoom);
	},

	_onZoom() {
		if (!this._map) return;
		this._updateTransform(this._map.getCenter(), this._map.getZoom());
	},

	_updateTransform(center, zoom) {
		const map = this._map;
		if (!map || !this._canvas || !this._center) return;

		const scale = map.getZoomScale(zoom, this._zoom);
		const viewHalf = map.getSize().multiplyBy(0.5 + this.options.padding);
		const currentCenterPoint = map.project(this._center, zoom);
		const topLeftOffset = viewHalf
			.multiplyBy(-scale)
			.add(currentCenterPoint)
			.subtract(map._getNewPixelOrigin(center, zoom));

		if (L.Browser.any3d) {
			L.DomUtil.setTransform(this._canvas, topLeftOffset, scale);
		} else {
			L.DomUtil.setPosition(this._canvas, topLeftOffset);
		}
	},

	_update() {
		const map = this._map;
		const canvas = this._canvas;
		if (!map || !canvas || !map._loaded) return;

		const p = this.options.padding;
		const size = map.getSize();
		const min = map.containerPointToLayerPoint(size.multiplyBy(-p)).round();
		const sz = size.multiplyBy(1 + p * 2).round();
		const dpr = window.devicePixelRatio || 1;

		this._topLeft = min;
		this._center = map.getCenter();
		this._zoom = map.getZoom();

		if (this._w !== sz.x || this._h !== sz.y || this._dpr !== dpr) {
			this._w = sz.x;
			this._h = sz.y;
			this._dpr = dpr;
			canvas.width = Math.round(sz.x * dpr);
			canvas.height = Math.round(sz.y * dpr);
			canvas.style.width = `${sz.x}px`;
			canvas.style.height = `${sz.y}px`;

			this._cols = Math.ceil(sz.x / DEDUPE_CELL) + 2;
			this._rows = Math.ceil(sz.y / DEDUPE_CELL) + 2;
			this._seen = new Uint8Array(this._cols * this._rows);
		}

		this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		L.DomUtil.setPosition(canvas, min);

		this._draw();
	},

	// ---------- drawing ----------
	_draw() {
		const map = this._map;
		const ctx = this._ctx;
		if (!map || !ctx || !this._w) return;

		const w = this._w;
		const h = this._h;
		ctx.clearRect(0, 0, w, h);

		const drawn = { x: [], y: [], s: [], i: [] };
		this._drawn = drawn;
		this._grid = null;

		const scale = map.options.crs.scale(map.getZoom());
		const origin = map.getPixelOrigin();
		const offX = origin.x + this._topLeft.x;
		const offY = origin.y + this._topLeft.y;
		const cols = this._cols;
		const seen = this._seen;

		this._groups.forEach((group) => {
			if (group.type === 'circle') {
				const r = group.r;
				const st = group.style;
				seen.fill(0);
				ctx.beginPath();

				for (let si = 0; si < group.series.length; si += 1) {
					const s = group.series[si];
					const { mx, my, n } = s;
					for (let i = 0; i < n; i += 1) {
						const x = mx[i] * scale - offX;
						if (!(x >= -r && x <= w + r)) continue; // also skips NaN
						const y = my[i] * scale - offY;
						if (!(y >= -r && y <= h + r)) continue;

						if (DEDUPE_ENABLED) {
							const cell = ((y / DEDUPE_CELL) | 0) * cols + ((x / DEDUPE_CELL) | 0);
							if (seen[cell]) continue;
							seen[cell] = 1;
						}

						ctx.moveTo(x + r, y);
						ctx.arc(x, y, r, 0, TWO_PI);

						if (s.hittable) {
							drawn.x.push(x);
							drawn.y.push(y);
							drawn.s.push(this._series.indexOf(s) === -1 ? 0 : s._idx !== undefined ? s._idx : (s._idx = this._series.indexOf(s)));
							drawn.i.push(i);
						}
					}
				}

				ctx.globalAlpha = st.fillOpacity !== undefined && st.fillOpacity !== null ? st.fillOpacity : 0.2;
				ctx.fillStyle = st.fillColor || st.color || '#3388ff';
				ctx.fill();

				if (st.weight !== 0) {
					ctx.globalAlpha = st.opacity !== undefined && st.opacity !== null ? st.opacity : 1;
					ctx.lineWidth = st.weight !== undefined && st.weight !== null ? st.weight : 3;
					ctx.strokeStyle = st.color || '#3388ff';
					ctx.stroke();
				}
				ctx.globalAlpha = 1;
				return;
			}

			if (group.type === 'diamond') {
				ctx.fillStyle = '#f75a00';
				ctx.strokeStyle = '#000000';
				ctx.lineWidth = 1;
				for (let si = 0; si < group.series.length; si += 1) {
					const s = group.series[si];
					const { mx, my, n } = s;
					for (let i = 0; i < n; i += 1) {
						const x = mx[i] * scale - offX;
						if (!(x >= -10 && x <= w + 10)) continue;
						const y = my[i] * scale - offY;
						if (!(y >= -10 && y <= h + 10)) continue;

						ctx.save();
						ctx.translate(x + 1, y + 1);
						ctx.rotate(DIAMOND_ANGLE);
						ctx.fillRect(-7, -7, 14, 14);
						ctx.strokeRect(-7.5, -7.5, 15, 15);
						ctx.restore();

						if (s.hittable) {
							drawn.x.push(x);
							drawn.y.push(y);
							drawn.s.push(s._idx !== undefined ? s._idx : (s._idx = this._series.indexOf(s)));
							drawn.i.push(i);
						}
					}
				}
				return;
			}

			// image sprite
			const sprite = loadSprite(group.url, () => this._schedule());
			if (!sprite.loaded) return;
			const [iw, ih] = group.size;
			const [ax, ay] = group.anchor;

			for (let si = 0; si < group.series.length; si += 1) {
				const s = group.series[si];
				const { mx, my, n } = s;
				for (let i = 0; i < n; i += 1) {
					const x = mx[i] * scale - offX;
					if (!(x >= -iw && x <= w + iw)) continue;
					const y = my[i] * scale - offY;
					if (!(y >= -ih && y <= h + ih)) continue;

					ctx.drawImage(sprite.img, x - ax, y - ay, iw, ih);

					if (s.hittable) {
						drawn.x.push(x);
						drawn.y.push(y);
						drawn.s.push(s._idx !== undefined ? s._idx : (s._idx = this._series.indexOf(s)));
						drawn.i.push(i);
					}
				}
			}
		});
	},

	// ---------- hit testing (lazy pixel grid) ----------
	_buildGrid() {
		const d = this._drawn;
		const grid = new Map();
		for (let k = 0; k < d.x.length; k += 1) {
			const key = (Math.floor(d.y[k] / HIT_CELL) + 64) * 4096 + (Math.floor(d.x[k] / HIT_CELL) + 64);
			const bucket = grid.get(key);
			if (bucket) bucket.push(k);
			else grid.set(key, [k]);
		}
		this._grid = grid;
	},

	_hitAt(layerPoint) {
		if (!this._drawn || !this._topLeft || !layerPoint) return -1;
		if (!this._grid) this._buildGrid();

		const px = layerPoint.x - this._topLeft.x;
		const py = layerPoint.y - this._topLeft.y;
		const cx = Math.floor(px / HIT_CELL);
		const cy = Math.floor(py / HIT_CELL);
		const R = this._hitCells;
		const d = this._drawn;
		let best = -1;

		for (let dy = -R; dy <= R; dy += 1) {
			for (let dx = -R; dx <= R; dx += 1) {
				const bucket = this._grid.get((cy + dy + 64) * 4096 + (cx + dx + 64));
				if (!bucket) continue;

				for (let b = 0; b < bucket.length; b += 1) {
					const k = bucket[b];
					if (k <= best) continue; // later = drawn on top

					const group = this._series[d.s[k]].group;
					const ox = px - d.x[k];
					const oy = py - d.y[k];
					const box = group.box;
					if (ox < box[0] || ox > box[2] || oy < box[1] || oy > box[3]) continue;
					if (group.type === 'circle' && ox * ox + oy * oy > (group.r + 2) * (group.r + 2)) continue;
					best = k;
				}
			}
		}
		return best;
	},

	// ---------- hover tooltip / click popup ----------
	_closeTip() {
		if (this._tip) {
			this._tip.remove();
			this._tip = null;
		}
		if (this._hoverKey !== null && this._map) {
			L.DomUtil.removeClass(this._map.getContainer(), 'leaflet-interactive');
		}
		this._hoverKey = null;
	},

	_onMouseMove(e) {
		this._lastMove = e;
		if (this._hoverRaf) return;
		this._hoverRaf = requestAnimationFrame(() => {
			this._hoverRaf = null;
			this._handleHover(this._lastMove);
		});
	},

	_handleHover(e) {
		const map = this._map;
		if (!map || !e || !this._drawn) return;
		if (map._animatingZoom) return;
		if (map.dragging && map.dragging.moving && map.dragging.moving()) return;

		const k = this._hitAt(e.layerPoint);
		if (k < 0) {
			if (this._hoverKey !== null) this._closeTip();
			return;
		}

		const d = this._drawn;
		const key = `${d.s[k]}:${d.i[k]}`;
		if (key === this._hoverKey) return;

		this._closeTip();
		this._hoverKey = key;

		const series = this._series[d.s[k]];
		const index = d.i[k];
		L.DomUtil.addClass(map.getContainer(), 'leaflet-interactive');

		if (series.tooltipAt) {
			const html = lazyHtml(() => series.tooltipAt(index))();
			if (html) {
				this._tip = L.tooltip({ direction: 'top', ...series.tooltipOptions })
					.setLatLng([series.lats[index], series.lngs[index]])
					.setContent(html);
				this._tip.openOn(map);
			}
		}
	},

	_onClick(e) {
		const map = this._map;
		if (!map || !this._drawn) return;

		const k = this._hitAt(e.layerPoint);
		if (k < 0) return;

		const d = this._drawn;
		const series = this._series[d.s[k]];
		if (!series.popupAt) return;

		const index = d.i[k];
		const html = lazyHtml(() => series.popupAt(index))();
		if (!html) return;

		const popupAnchor = series.group.popupAnchor || [0, 0];
		L.popup({ offset: L.point(popupAnchor), ...series.popupOptions })
			.setLatLng([series.lats[index], series.lngs[index]])
			.setContent(html)
			.openOn(map);
	},
});

// ============================================================
// PERFORMANCE: BULK LAYER
//
// `items` is a flat, memoized list of descriptors:
//   { kind: 'points'  | 'markers', lats[], lngs[], ... }  -> ONE canvas
//   { kind: 'polyline', positions, style, tooltip?, ... }  -> Leaflet
//        polylines (positions may be a multi-polyline, i.e. an array
//        of arrays of [lat, lng], so many segments = ONE layer).
// Polylines are few (one per trajectory / band), are drawn on the
// map's canvas renderer, are added in time-sliced chunks and get their
// tooltips lazily.
// ============================================================
const BULK_FRAME_BUDGET_MS = 8;
const BULK_MIN_PER_FRAME = 20;

const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

const BulkLayer = ({ items }) => {
	const map = useMap();

	useEffect(() => {
		if (!items || items.length === 0) return undefined;

		const seriesItems = [];
		const lineItems = [];
		items.forEach((item) => {
			if (item.kind === 'polyline') lineItems.push(item);
			else seriesItems.push(item);
		});

		let pointsLayer = null;
		if (seriesItems.length > 0) {
			pointsLayer = new CanvasPointsLayer(seriesItems);
			pointsLayer.addTo(map);
		}

		let group = null;
		let cancelled = false;
		let rafId = null;
		let handleMouseOver = null;
		let handleClick = null;

		if (lineItems.length > 0) {
			group = L.featureGroup().addTo(map);
			let cursor = 0;

			handleMouseOver = (e) => {
				const layer = e.layer;
				const item = layer && layer._bulkItem;
				if (!item || !item.tooltip || layer._bulkTooltipBound) return;
				layer._bulkTooltipBound = true;
				layer.bindTooltip(lazyHtml(item.tooltip), item.tooltipOptions || {});
				layer.openTooltip(e.latlng);
			};

			handleClick = (e) => {
				const layer = e.layer;
				const item = layer && layer._bulkItem;
				if (!item || !item.popup || layer._bulkPopupBound) return;
				layer._bulkPopupBound = true;
				layer.bindPopup(lazyHtml(item.popup), item.popupOptions || {});
				layer.openPopup(e.latlng);
			};

			group.on('mouseover', handleMouseOver);
			group.on('click', handleClick);

			const step = () => {
				if (cancelled) return;

				const frameStart = now();
				let builtThisFrame = 0;

				while (cursor < lineItems.length) {
					const item = lineItems[cursor];
					cursor += 1;
					builtThisFrame += 1;

					const layer = L.polyline(item.positions, item.style);
					if (item.tooltip || item.popup) layer._bulkItem = item;
					group.addLayer(layer);

					if (
						builtThisFrame >= BULK_MIN_PER_FRAME &&
						now() - frameStart > BULK_FRAME_BUDGET_MS
					) {
						break;
					}
				}

				if (cursor < lineItems.length) {
					rafId = requestAnimationFrame(step);
				}
			};

			rafId = requestAnimationFrame(step);
		}

		return () => {
			cancelled = true;
			if (rafId) cancelAnimationFrame(rafId);
			if (group) {
				group.off('mouseover', handleMouseOver);
				group.off('click', handleClick);
				map.removeLayer(group);
			}
			if (pointsLayer) {
				map.removeLayer(pointsLayer);
			}
		};
	}, [map, items]);

	return null;
};

BulkLayer.propTypes = {
	items: PropTypes.array,
};

BulkLayer.defaultProps = {
	items: [],
};

// ============================================================
// MAP AUTO-CENTER
// ============================================================
const MapAutoCenter = ({ trajectories, pidsMarkers, enabled }) => {
	const map = useMap();

	useEffect(() => {
		if (!enabled) return;

		let minLat = Infinity;
		let maxLat = -Infinity;
		let minLng = Infinity;
		let maxLng = -Infinity;
		let count = 0;

		const extend = (lat, lng) => {
			if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
			if (lat < minLat) minLat = lat;
			if (lat > maxLat) maxLat = lat;
			if (lng < minLng) minLng = lng;
			if (lng > maxLng) maxLng = lng;
			count += 1;
		};

		(trajectories || []).forEach((trajectory) => {
			(trajectory.points || []).forEach((point) => extend(point.lat, point.lng));
			if (trajectory.peakCoordinates) {
				extend(
					parseFloat(trajectory.peakCoordinates.latitude),
					parseFloat(trajectory.peakCoordinates.longitude)
				);
			}
		});

		if (count === 0) {
			(pidsMarkers || []).forEach((pid) => extend(pid.lat, pid.lng));
		}

		if (count === 0) return;

		const bounds = L.latLngBounds([minLat, minLng], [maxLat, maxLng]);

		map.invalidateSize();
		map.fitBounds(bounds, {
			padding: [40, 40],
			maxZoom: 16,
			animate: false,
		});
	}, [map, trajectories, pidsMarkers, enabled]);

	return null;
};

MapAutoCenter.propTypes = {
	trajectories: PropTypes.array,
	pidsMarkers: PropTypes.array,
	enabled: PropTypes.bool,
};

MapAutoCenter.defaultProps = {
	trajectories: [],
	pidsMarkers: [],
	enabled: false,
};

// ============================================================
// SEAMAP
// ============================================================

const SeaMap = ({
	center,
	zoom,
	style,
	mapContainerProps,
	layersControlChildren,
	mapChildren,
	detectionTrack,
	detectionMeta,
	detectionLabel,
	detectionInfo,
	trackColor,
	pipelineTracks,
	fiberTracks,
	pipelineBorderTracks,
	optionalPathTracks,
	kmpMarkers,
	// wbMarkers,
	// tpMarkers,
	// tlpMarkers,
	chMarkers,
	detectionTrajectories,
	distanceRangeTracks,
	asnData,
	coordinatesData,
	showTrajectoryPlayControl,
	autoCenter,
	showFullscreenControl,
	showTopToolbar,
	showBottomInfoBar,
	pidsRegionData,
	pigPids,
	enableAISVessels,
	showAISVessels,
	aisVesselsUrl,
	aisPollIntervalMs,
	aisBbox,
	aisIncludeKinds,
	aisMaxVessels,
	aisFallbackBbox,
	aisstreamApiKey,
	onDeleteCoordinate,

}) => {
	const hasTrack =
		Array.isArray(detectionTrack) &&
		detectionTrack.length > 0;

	const color =
		trackColor ||
		TRACK_COLORS[0];

	const trackPositions = hasTrack
		? detectionTrack.map((point) => [
			point.lat,
			point.lon
		])
		: [];

	const firstPoint = hasTrack
		? detectionTrack[0]
		: null;

	const cpaPoint = hasTrack
		? (
			detectionTrack.find(
				(point) => point.isCPA
			) ||
			detectionTrack[
				detectionTrack.length - 1
			]
		)
		: null;

	const [playAllTrajectories, setPlayAllTrajectories] = useState(false);

	const [animatedTrajectoryPoints, setAnimatedTrajectoryPoints] = useState({});

	const trajectoryIdsKey = detectionTrajectories.map((trajectory) => trajectory.id).join(',');

	useEffect(() => {
		if (!playAllTrajectories || detectionTrajectories.length === 0) {
			setAnimatedTrajectoryPoints({});
			return;
		}

		const validPointsByTrajectory = {};
		detectionTrajectories.forEach((trajectory) => {
			validPointsByTrajectory[trajectory.id] = (trajectory.points || [])
				.filter((point) => point && point.lat != null && point.lng != null)
				.map((point) => [point.lat, point.lng]);
		});

		setAnimatedTrajectoryPoints((prev) => {
			const next = {};
			Object.keys(validPointsByTrajectory).forEach((id) => {
				const points = validPointsByTrajectory[id];
				if (prev[id] && prev[id].length > 0) {
					next[id] = prev[id].length <= points.length
						? prev[id]
						: points.slice(0, prev[id].length);
				} else if (points.length > 0) {
					next[id] = [points[0]];
				}
			});
			return next;
		});

		const interval = setInterval(() => {
			let allComplete = true;

			setAnimatedTrajectoryPoints((prev) => {
				const next = { ...prev };
				Object.keys(validPointsByTrajectory).forEach((id) => {
					const points = validPointsByTrajectory[id];
					const current = next[id] || [];
					if (current.length < points.length) {
						next[id] = [...current, points[current.length]];
						allComplete = false;
					}
				});
				return next;
			});

			if (allComplete) {
				clearInterval(interval);
			}
		}, 500);

		return () => clearInterval(interval);
	}, [playAllTrajectories, trajectoryIdsKey]);

	// ---- Source (detected) trajectory toggles ----
	const [showDetectionPoints, setShowDetectionPoints] = useState(false);
	const [showFibreDetection, setShowFibreDetection] = useState(true);
	const [showReferenceCoordinate, setShowReferenceCoordinate] = useState(false);
	const [showDetectionPolyline, setShowDetectionPolyline] = useState(false);
	const [showCoordinateFibreLine, setShowCoordinateFibreLine] = useState(false);
	const [showShipFibreLink, setShowShipFibreLink] = useState(false);

	// ---- Port trajectory toggles ----
	const [showPortPoints, setShowPortPoints] = useState(false);
	const [showPortPolyline, setShowPortPolyline] = useState(false);
	// ---- ASN vessel toggles ----
	const [showAsnPoints, setShowAsnPoints] = useState(false);
	const [showAsnLines, setShowAsnLines] = useState(false);
	// ---- Distance Range toggle ----
	const [showDistanceRange, setShowDistanceRange] = useState(false);

	// ASNdata item = { name, latitude[], longitude[], speed[], datetime[] } (index-aligned)
	// -> { id, name, points: [{ lat, lng, index, speed, datetime }] }
	const asnTrajectories = useMemo(() => {
		const toArray = (v) => {
			if (Array.isArray(v)) return v;
			if (v === null || v === undefined || v === '') return [];
			return [v];
		};

		return (asnData || [])
			.map((item, itemIndex) => {
				const lats = toArray(item?.latitude);
				const lngs = toArray(item?.longitude);
				const speeds = toArray(item?.speed);
				const times = toArray(item?.datetime);

				const points = [];
				lats.forEach((lat, index) => {
					const lng = lngs[index];
					if (
						lat == null || lng == null ||
						!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))
					) {
						return;
					}
					points.push({
						lat: Number(lat),
						lng: Number(lng),
						index,
						speed: speeds[index] ?? null,
						datetime: times[index] ?? null,
					});
				});

				return {
					id: `asn-${itemIndex}-${item?.name ?? ''}`,
					name: item?.name ? item.name : 'No Data',
					points,
				};
			})
			.filter((asn) => asn.points.length > 0);
	}, [asnData]);

	const wrapperRef = useRef(null);
	const positionSpanRef = useRef(null);

	const [mapInstance, setMapInstance] = useState(null);

	const handleMapReady = useCallback((map) => {
		setMapInstance(map);
	}, []);

	const [layerVisibility, setLayerVisibility] = useState({
		seaMarks: true,
		harbours: true,
		aerialPhoto: false,
		coordinateGrid: true,
		marineTraffic: true,
		compassRose: false,
		waterDepth: false,
		depthContours: true,
		aisVessels: !!showAISVessels,
	});

	const toggleLayer = useCallback((key) => {
		setLayerVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
	}, []);

	const depthContoursParams = useMemo(() => ({
		layers: 'openseamap:contour2,openseamap:contour',
		version: '1.1.0',
		format: 'image/png',
		transparent: true,
	}), []);

	const waterDepthParams = useMemo(() => ({
		layers: 'gebco2021:gebco_2021',
		version: '1.1.1',
		format: 'image/png',
		transparent: true,
	}), []);

	const allPidsData = useMemo(() => {
		if (typeof window === 'undefined' || !dashboardtype) return [];
		try {
			const raw = localStorage.getItem(`${dashboardtype.toUpperCase()}_PIDS`);
			const parsed = raw ? JSON.parse(raw) : [];
			return Array.isArray(parsed) ? parsed : [];
		} catch (err) {
			return [];
		}
	}, []);

	const pidsMarkers = useMemo(() => {
		let source = [];

		if (
			(allPidsData.length > 0 && pidsRegionData && pidsRegionData.length === 0) ||
			pidsRegionData === undefined
		) {
			source = allPidsData;
		} else if (pidsRegionData && pidsRegionData.length > 0) {
			source = pidsRegionData;
		}

		return source
			.map((item) => ({
				lat: parseFloat(item.latitude),
				lng: parseFloat(item.longitude),
				value: item.value,
				label: item.label,
			}))
			.filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng));
	}, [allPidsData, pidsRegionData]);

	const isValidLatLng = (lat, lng) =>
		typeof lat === 'number' && !isNaN(lat) &&
		typeof lng === 'number' && !isNaN(lng);

	const safeCenter = isValidLatLng(center?.lat, center?.lng)
		? [center.lat, center.lng]
		: Array.isArray(center) && isValidLatLng(center[0], center[1])
			? center
			: [0, 0];

	// ============================================================
	// DISTANCE RANGE ITEMS
	//   • band      -> one polyline per track (transparent, wide)
	//   • endpoints -> ONE points-series per track (S / E circles)
	// ============================================================
	const distanceRangeItems = useMemo(() => {
		const items = [];
		if (!showDistanceRange || !distanceRangeTracks) return items;

		distanceRangeTracks.forEach((track, index) => {
			const bandColor = track.color || ALGORITHM_COLORS[index % ALGORITHM_COLORS.length];

			const tooltipBody = (point, titleKey, titleFallback) => (
				<>
					{titleKey && (
						<>
							<strong>
								{(langdata && langdata[titleKey]) ? langdata[titleKey] : titleFallback}
							</strong>
							<br />
						</>
					)}
					{(langdata && langdata.EventType ? langdata.EventType : 'Event Type')}
					:
					{' '}
					{track.eventType || 'No Data'}
					<br />
					{(langdata && langdata.Channel ? langdata.Channel : 'Channel')}
					:
					{' '}
					{track.channelName || 'No Data'}
					<br />
					{['superadmin'].includes(permission) && (
						<>
							{(langdata && langdata.Algorithm ? langdata.Algorithm : 'Algorithm')}
							:
							{' '}
							{track.algorithmName || 'No Data'}
							<br />
						</>
					)}
					{(langdata && langdata.distanceRange ? langdata.distanceRange : 'Distance Range')} (m)
					:
					{' '}
					{Number(track.minDistance).toFixed(2)}
					{' - '}
					{Number(track.maxDistance).toFixed(2)}
					{point && (
						<>
							<br />
							{(langdata && langdata.FibreDistance ? langdata.FibreDistance + ' (m)' : 'Fibre Distance (m)')}
							:
							{' '}
							{Number(point.distance).toFixed(2)}
							<br />
							{(langdata && langdata.Latitude ? langdata.Latitude : 'Latitude')}
							:
							{' '}
							{point.lat}
							<br />
							{(langdata && langdata.Longitude ? langdata.Longitude : 'Longitude')}
							:
							{' '}
							{point.lng}
						</>
					)}
				</>
			);

			items.push({
				kind: 'polyline',
				positions: track.positions,
				style: {
					color: bandColor,
					lineCap: 'butt',
					opacity: 0.35,
					weight: 25,
				},
				tooltipOptions: { sticky: true },
				tooltip: () => tooltipBody(null),
			});

			const endpoints = [
				[track.startPoint, 'DistanceRangeStart', 'Distance Range Start'],
				[track.endPoint, 'DistanceRangeEnd', 'Distance Range End'],
			].filter(([p]) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));

			if (endpoints.length > 0) {
				items.push({
					kind: 'points',
					lats: endpoints.map((e) => e[0].lat),
					lngs: endpoints.map((e) => e[0].lng),
					radius: 5,
					style: {
						color: '#ffffff',
						weight: 1,
						fillColor: bandColor,
						fillOpacity: 0.9,
					},
					tooltipOptions: { direction: 'top', offset: [0, -4] },
					tooltipAt: (i) => tooltipBody(endpoints[i][0], endpoints[i][1], endpoints[i][2]),
				});
			}
		});

		return items;
	}, [showDistanceRange, distanceRangeTracks]);

	// ============================================================
	// DETECTION ITEMS — everything passed as ARRAYS
	//
	//   • source points   -> ONE points-series per trajectory
	//   • port points     -> ONE points-series per trajectory
	//   • start markers   -> ONE markers-series for ALL trajectories
	//   • port start mkrs -> ONE markers-series for ALL trajectories
	//   • peak (fibre)    -> ONE diamond-series for ALL trajectories
	//   • ship-fibre link -> ONE multi-polyline per trajectory
	// Hover / popup content is produced lazily by index.
	// ============================================================
	const detectionItems = useMemo(() => {
		const items = [];

		const startMarkers = { lats: [], lngs: [], refs: [] };
		const portStartMarkers = { lats: [], lngs: [], refs: [] };
		const peaks = { lats: [], lngs: [], dists: [] };

		const startPopup = (trajectory) => (
			<>
				<strong>
					{(langdata && langdata.StartingPoint)
						? langdata.StartingPoint
						: 'Starting Point'}
				</strong>
				<br />
				{trajectory.channelName ||
					trajectory.label ||
					(langdata && langdata.RealtimeDetection
						? langdata.RealtimeDetection
						: 'Detection')}
			</>
		);

		(detectionTrajectories || []).forEach((trajectory, trajIndex) => {

			const trajColor =
				colorForAlgorithmId(trajectory.detectionAlgorithmTypeId) ||
				trajectory.color ||
				ALGORITHM_COLORS[trajIndex % ALGORITHM_COLORS.length];

			const points = trajectory.points || [];
			const trajFirstPoint = points[0];

			const trajInfo = {
				eventType: trajectory.eventType,
				detectionAlgorithmTypeName: trajectory.algorithmName,
				channelName: trajectory.channelName,
				pidsName: trajectory.pidsName,
				peakDistance: trajectory.peakDistance,
			};

			const pointStyle = {
				color: '#ffffff',
				weight: 1,
				fillColor: trajColor,
				fillOpacity: 0.9
			};

			const buildInfo = (idx, extra) => ({
				...trajInfo,
				cpaSourceDistance: pickValueForIndex(trajectory.cpaSourceDistance, idx),
				sourceSpeedms: pickValueForIndex(trajectory.sourceSpeedKts, idx),
				startTimeHover: pickValueForIndex(trajectory.startTimeHover, idx),
				...(extra || {}),
			});

			// ---- source lines ----
			if (points.length > 1 && showDetectionPolyline) {
				items.push({
					kind: 'polyline',
					positions: points.map((point) => [point.lat, point.lng]),
					style: {
						color: trajColor,
						weight: 3,
						opacity: 0.85
					},
				});
			}

			// ---- source points (index 0 is drawn as the start marker) ----
			if (showDetectionPoints && points.length > 1) {
				const rest = points.slice(1);
				items.push({
					kind: 'points',
					lats: rest.map((p) => p.lat),
					lngs: rest.map((p) => p.lng),
					radius: 5,
					style: pointStyle,
					tooltipOptions: { direction: 'top', offset: [0, -4] },
					tooltipAt: (i) => (
						<DetectionHoverContent
							info={buildInfo(i + 1)}
							point={rest[i]}
						/>
					),
				});
			}

			// ---- source start point: circle + (shared) start marker ----
			if (trajFirstPoint) {
				items.push({
					kind: 'points',
					lats: [trajFirstPoint.lat],
					lngs: [trajFirstPoint.lng],
					radius: 5,
					style: pointStyle,
				});

				startMarkers.lats.push(trajFirstPoint.lat);
				startMarkers.lngs.push(trajFirstPoint.lng);
				startMarkers.refs.push({
					trajectory,
					point: trajFirstPoint,
					info: buildInfo(0),
				});
			}

			// ================= PORT TRAJECTORY (dotted) =================
			const portPoints = (trajectory.portPoints || []).filter(
				(p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng)
			);

			const buildPortInfo = (point) => buildInfo(point.index, {
				hoverLat: point.lat,
				hoverLng: point.lng,
			});

			// PORT LINES — "Mirror Lines" overlay
			if (portPoints.length > 1 && showPortPolyline) {
				items.push({
					kind: 'polyline',
					positions: portPoints.map((p) => [p.lat, p.lng]),
					style: {
						color: trajColor,
						weight: 2,
						opacity: 0.85,
						dashArray: '2,8',
						lineCap: 'round'
					},
				});
			}

			// PORT POINTS — "Mirror Points" overlay
			if (showPortPoints && portPoints.length > 0) {
				if (portPoints.length > 1) {
					const portRest = portPoints.slice(1);
					items.push({
						kind: 'points',
						lats: portRest.map((p) => p.lat),
						lngs: portRest.map((p) => p.lng),
						radius: 5,
						style: pointStyle,
						tooltipOptions: { direction: 'top', offset: [0, -4] },
						tooltipAt: (i) => (
							<DetectionHoverContent
								info={buildPortInfo(portRest[i])}
								point={portRest[i]}
							/>
						),
					});
				}

				const portFirst = portPoints[0];

				items.push({
					kind: 'points',
					lats: [portFirst.lat],
					lngs: [portFirst.lng],
					radius: 5,
					style: pointStyle,
				});

				portStartMarkers.lats.push(portFirst.lat);
				portStartMarkers.lngs.push(portFirst.lng);
				portStartMarkers.refs.push({
					trajectory,
					point: portFirst,
					info: buildPortInfo(portFirst),
				});
			}
			// ================= END PORT TRAJECTORY =================

			const peakLat =
				trajectory.peakCoordinates
					? parseFloat(trajectory.peakCoordinates.latitude)
					: NaN;
			const peakLng =
				trajectory.peakCoordinates
					? parseFloat(trajectory.peakCoordinates.longitude)
					: NaN;
			const peakDistance =
				trajectory.peakDistance
					? Number(trajectory.peakDistance).toFixed(2)
					: null;

			if (Number.isFinite(peakLat) && Number.isFinite(peakLng) && showFibreDetection) {
				peaks.lats.push(peakLat);
				peaks.lngs.push(peakLng);
				peaks.dists.push(trajectory && trajectory.peakDistance ? peakDistance : null);
			}

			// Ship-fibre links: ALL segments of a trajectory = ONE multi-polyline.
			if (Number.isFinite(peakLat) && Number.isFinite(peakLng) && showShipFibreLink) {
				const segments = [];
				points.forEach((point) => {
					if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) {
						return;
					}
					segments.push([
						[point.lat, point.lng],
						[peakLat, peakLng],
					]);
				});

				if (segments.length > 0) {
					items.push({
						kind: 'polyline',
						positions: segments,
						style: {
							color: trajColor,
							weight: 3.5,
							dashArray: '4,5',
							opacity: 1,
						},
					});
				}
			}
		});

		// ---- shared marker series (one array for ALL trajectories) ----
		if (startMarkers.lats.length > 0) {
			items.push({
				kind: 'markers',
				icon: vesselCpaIcon,
				lats: startMarkers.lats,
				lngs: startMarkers.lngs,
				tooltipOptions: { direction: 'top', offset: [0, -40] },
				tooltipAt: (i) => (
					<DetectionHoverContent
						info={startMarkers.refs[i].info}
						point={startMarkers.refs[i].point}
					/>
				),
				popupAt: (i) => startPopup(startMarkers.refs[i].trajectory),
			});
		}

		if (portStartMarkers.lats.length > 0) {
			items.push({
				kind: 'markers',
				icon: vesselCpaIcon,
				lats: portStartMarkers.lats,
				lngs: portStartMarkers.lngs,
				tooltipOptions: { direction: 'top', offset: [0, -40] },
				tooltipAt: (i) => (
					<DetectionHoverContent
						info={portStartMarkers.refs[i].info}
						point={portStartMarkers.refs[i].point}
					/>
				),
				popupAt: (i) => startPopup(portStartMarkers.refs[i].trajectory),
			});
		}

		if (peaks.lats.length > 0) {
			items.push({
				kind: 'markers',
				shape: 'diamond',
				lats: peaks.lats,
				lngs: peaks.lngs,
				tooltipOptions: { direction: 'top', offset: [0, -40] },
				tooltipAt: (i) => (
					<>
						<strong>{(langdata && langdata.FibreDetection ? langdata.FibreDetection : 'Fibre Detection')}</strong>
						<br />
						{(langdata && langdata.FibreDistance ? langdata.FibreDistance + ' (m)' : 'Fibre Distance (m)')}
						:
						{' '}
						{peaks.dists[i] ? peaks.dists[i] : 'No Data'}
						{' '}
						<br />
					</>
				),
			});
		}

		return items;
	}, [
		detectionTrajectories,
		showDetectionPolyline,
		showDetectionPoints,
		showPortPolyline,
		showPortPoints,
		showFibreDetection,
		showShipFibreLink,
	]);

	// ============================================================
	// ASN ITEMS — one points-series (+ one line) per vessel,
	// one shared markers-series for every vessel's first point.
	// ============================================================
	const asnItems = useMemo(() => {
		const items = [];
		const firstMarkers = { lats: [], lngs: [], refs: [] };

		asnTrajectories.forEach((asn, asnIndex) => {
			const asnColor =
				colorForAlgorithmId(asn.name) ||
				ALGORITHM_COLORS[asnIndex % ALGORITHM_COLORS.length];

			const points = asn.points || [];
			const asnPointStyle = {
				color: '#ffffff',
				weight: 1,
				fillColor: asnColor,
				fillOpacity: 0.9
			};

			if (points.length > 1 && showAsnLines) {
				items.push({
					kind: 'polyline',
					positions: points.map((p) => [p.lat, p.lng]),
					style: { color: asnColor, weight: 3, opacity: 0.85 },
				});
			}

			if (showAsnPoints) {
				if (points.length > 1) {
					const rest = points.slice(1);
					items.push({
						kind: 'points',
						lats: rest.map((p) => p.lat),
						lngs: rest.map((p) => p.lng),
						radius: 5,
						style: asnPointStyle,
						tooltipOptions: { direction: 'top', offset: [0, -4] },
						tooltipAt: (i) => (
							<AsnHoverContent name={asn.name} point={rest[i]} />
						),
					});
				}

				const first = points[0];
				if (first) {
					items.push({
						kind: 'points',
						lats: [first.lat],
						lngs: [first.lng],
						radius: 5,
						style: asnPointStyle,
					});

					firstMarkers.lats.push(first.lat);
					firstMarkers.lngs.push(first.lng);
					firstMarkers.refs.push({ name: asn.name, point: first });
				}
			}
		});

		if (firstMarkers.lats.length > 0) {
			items.push({
				kind: 'markers',
				icon: aisDataVesselIcon,
				lats: firstMarkers.lats,
				lngs: firstMarkers.lngs,
				tooltipOptions: { direction: 'top', offset: [0, -40] },
				tooltipAt: (i) => (
					<AsnHoverContent name={firstMarkers.refs[i].name} point={firstMarkers.refs[i].point} />
				),
				popupAt: (i) => (
					<>
						<strong>
							{(langdata && langdata.StartingPoint) ? langdata.StartingPoint : 'Starting Point'}
						</strong>
						<br />
						{firstMarkers.refs[i].name}
					</>
				),
			});
		}

		return items;
	}, [asnTrajectories, showAsnPoints, showAsnLines]);


	return (
		<div
			ref={wrapperRef}
			style={{
				position: 'relative',
				height: '100%',
				width: '100%'
			}}
		>
			{showTopToolbar && (
				<TopToolbar
					map={mapInstance}
					layerVisibility={layerVisibility}
					onToggleLayer={toggleLayer}
					showAisMenuItem={enableAISVessels}
				/>
			)}

			<MapContainer
				center={safeCenter}
				zoom={zoom}
				maxZoom={22}
				scrollWheelZoom={true}
				preferCanvas={true}
				style={
					style || {
						height: '100%',
						width: '100%'
					}
				}
				{...mapContainerProps}
			>

				<MapReadyBridge onReady={handleMapReady} />
				<MapAutoCenter trajectories={detectionTrajectories} pidsMarkers={pidsMarkers} enabled={autoCenter} />
				{showBottomInfoBar && <MapPositionBridge targetRef={positionSpanRef} />}

				{showFullscreenControl && (
					<FullscreenControl position="topleft" fullscreenTargetRef={wrapperRef} />
				)}

				{layerVisibility.aerialPhoto ? (
					<TileLayer
						url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
						attribution='Tiles &copy; <a href="https://www.esri.com">Esri</a> — Source: Esri, Maxar, Earthstar Geographics'
						maxZoom={22}
						maxNativeZoom={19}
					/>
				) : (
					<TileLayer
						attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
						maxZoom={22}
						maxNativeZoom={19}
					/>
				)}

				{layerVisibility.seaMarks && (
					<TileLayer
						attribution='&copy; <a href="http://openseamap.org">OpenSeaMap</a> contributors'
						url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png"
						maxZoom={22}
						maxNativeZoom={18}
					/>
				)}

				{layerVisibility.waterDepth && (
					<WMSTileLayer
						url="https://geoserver.openseamap.org/geoserver/gwc/service/wms"
						params={waterDepthParams}
						opacity={0.8}
						attribution="GEBCO 2021 Bathymetry"
					/>
				)}

				{layerVisibility.depthContours && (
					<WMSTileLayer
						url="https://depth.openseamap.org/geoserver/openseamap/wms"
						params={depthContoursParams}
						attribution='&copy; <a href="https://www.openseamap.org">OpenSeaMap</a> depth contours'
					/>
				)}

				{layerVisibility.marineTraffic && (
					<TileLayer
						url="https://tiles.marinetraffic.com/ais_helpers/shiptilesingle.aspx?output=png&sat=1&grouping=shiptype&tile_size=512&legends=1&zoom={z}&X={x}&Y={y}"
						tileSize={512}
						attribution='&copy; <a href="https://www.marinetraffic.com">MarineTraffic</a>'
					/>
				)}

				{layerVisibility.coordinateGrid && <CoordinateGrid />}

				{layerVisibility.harbours && <HarbourLayer />}

				{layerVisibility.compassRose && <CompassRoseControl />}

				{enableAISVessels && layerVisibility.aisVessels && (
					<AisVesselsLayer
						vesselsUrl={aisVesselsUrl}
						pollIntervalMs={aisPollIntervalMs}
						fixedBbox={aisBbox}
						includeKinds={aisIncludeKinds}
						maxVessels={aisMaxVessels}
						fallbackBbox={aisFallbackBbox}
						aisstreamApiKey={aisstreamApiKey}
					/>
				)}

				{pidsMarkers.map((item, index) => (
					<React.Fragment key={`pids-${index}`}>
						<Marker position={[item.lat, item.lng]} icon={pidsIcon}>
							{item.label && (
								<Tooltip direction="top" offset={[0, -28]}>
									{item.label}
								</Tooltip>
							)}
							<Popup>{item.label}</Popup>
						</Marker>

						{pigPids &&
							pigPids.map(
								(pigValue, pigIndex) =>
									pigValue === item.value && (
										<Circle
											key={`pids-flag-${index}-${pigIndex}`}
											center={[item.lat, item.lng]}
											radius={300}
											pathOptions={{ color: 'red', fillOpacity: 0 }}
										/>
									)
							)}
					</React.Fragment>
				))}

				{showReferenceCoordinate && coordinatesData && coordinatesData.map((coordinate) => (
					<CoordinateMarker
						key={`coordinate-${coordinate.id}`}
						coordinate={coordinate}
						onDelete={onDeleteCoordinate}
					/>
				))}

				{showReferenceCoordinate && coordinatesData && coordinatesData.map((coordinate) => (
					<FibreCoordinateMarker
						key={`fibre-coordinate-${coordinate.id}`}
						coordinate={coordinate}
						onDelete={onDeleteCoordinate}
					/>
				))}

				{showCoordinateFibreLine && coordinatesData && coordinatesData.map((coordinate) => {
					const hasCoord = Number.isFinite(coordinate.latitude) && Number.isFinite(coordinate.longitude);

					const hasFibre = Number.isFinite(coordinate.fibrelatitude) && Number.isFinite(coordinate.fibrelongitude);

					if (!hasCoord || !hasFibre) return null;

					return (
						<Polyline
							key={`coordinate-fibre-line-${coordinate.id}`}
							positions={[
								[coordinate.latitude, coordinate.longitude],
								[coordinate.fibrelatitude, coordinate.fibrelongitude],
							]}
							pathOptions={{
								color: '#ff00ff',
								weight: 2,
								dashArray: '6,6',
								opacity: 0.85,
							}}
						/>
					);
				})}

				<LayersControl position="topright">

					<Overlay
						checked
						name={oneviewtypeLabeluper}
					>
						<>
							{pipelineTracks &&
								pipelineTracks.map(
									(track, index) => (
										<Polyline
											key={`pipeline-${index}`}
											pathOptions={pipecolor}
											weight={4}
											positions={track}
										/>
									)
								)}
						</>
					</Overlay>

					<Overlay name="Fiber">
						<>
							{fiberTracks &&
								fiberTracks.map(
									(track, index) => (
										<Polyline
											key={`fiber-${index}`}
											pathOptions={odmetercolor}
											weight={4}
											positions={track}
										/>
									)
								)}
						</>
					</Overlay>

					<Overlay name="Optional Path">
						<>
							{optionalPathTracks &&
								optionalPathTracks.map(
									(track, index) => (
										<Polyline
											key={`optional-path-${index}`}
											pathOptions={optionalcolor}
											weight={4}
											positions={track}
										/>
									)
								)}
						</>
					</Overlay>

					<Overlay name={(langdata && langdata.KMP) ? langdata.KMP : 'KMP'}>
						<MarkerClusterGroup>
							{kmpMarkers && kmpMarkers.map((marker, index) => (
								<Marker key={`kmp-${index}`} position={marker.geocode} icon={KMPicon}>
									<Popup>
										{marker.pop}
										<div>{(langdata && langdata.Landmark) ? langdata.Landmark : 'Landmark'}: {marker.landmarkDescription}</div>
										<div>{(langdata && langdata.Chainage) ? langdata.Chainage + ' (m)' : 'Chainage (m)'}: {marker.pipelineChainage}</div>
										<div>{(langdata && langdata.FiberLength) ? langdata.FiberLength : 'Fiber Length (m)'}: {marker.ODMeter}</div>
									</Popup>
								</Marker>
							))}
						</MarkerClusterGroup>
					</Overlay>

					{/* WB / TP / TLP overlays are disabled (same as before) — their
					    icons/imports are commented at the top of this file; restore
					    them together if you need those layers again. */}

					<Overlay name={(langdata && langdata.CH) ? langdata.CH : 'CH'}>
						<MarkerClusterGroup>
							{chMarkers && chMarkers.map((marker, index) => (
								<Marker key={`ch-${index}`} position={marker.geocode} icon={CHicon}>
									<Popup>
										{marker.pop}
										<div>{(langdata && langdata.Landmark) ? langdata.Landmark : 'Landmark'}: {marker.landmarkDescription}</div>
										<div>{(langdata && langdata.Chainage) ? langdata.Chainage : 'Chainage'}: {marker.pipelineChainage}</div>
									</Popup>
								</Marker>
							))}
						</MarkerClusterGroup>
					</Overlay>

					<Overlay name="System Border">
						<>
							{pipelineBorderTracks &&
								pipelineBorderTracks.map(
									(track, index) => (
										<Polyline
											key={`pipeline-border-${index}`}
											pathOptions={bordercolor}
											opacity={0.35}
											weight={25}
											positions={track}
										/>
									)
								)}
						</>
					</Overlay>
					<Overlay
						name={(langdata && langdata.DistanceRange) ? langdata.DistanceRange : 'Distance Range'}
					>
						<Polyline
							positions={[]}
							eventHandlers={{
								add: () => setShowDistanceRange(true),
								remove: () => setShowDistanceRange(false)
							}}
						/>
					</Overlay>

					{showTrajectoryPlayControl && detectionTrajectories.length > 0 && (
						<Overlay name={(langdata && langdata.Play) ? langdata.Play : 'Play'}>
							<Polyline
								positions={[]}
								eventHandlers={{
									add: () => setPlayAllTrajectories(true),
									remove: () => setPlayAllTrajectories(false)
								}}
							/>
						</Overlay>
					)}

					{detectionTrajectories.length > 0 && (
						<>
							<Overlay
								name={(langdata && langdata.Points) ? langdata.Points : 'Points'}
							>
								<Polyline
									positions={[]}
									eventHandlers={{
										add: () => setShowDetectionPoints(true),
										remove: () => setShowDetectionPoints(false)
									}}
								/>
							</Overlay>

							{/* ---- MIRROR (PORT) POINTS ---- */}
							<Overlay
								{...(showPortPolyline ? { checked: true } : {})}
								name={(langdata && langdata.MirrorPoints) ? langdata.MirrorPoints : 'Mirror Points'}
							>
								<Polyline
									positions={[]}
									eventHandlers={{
										add: () => setShowPortPoints(true),
										remove: () => setShowPortPoints(false)
									}}
								/>
							</Overlay>

							<Overlay
								checked
								name={(langdata && langdata.FibreDetection) ? langdata.FibreDetection : 'Fibre Detection'}
							>
								<Polyline
									positions={[]}
									eventHandlers={{
										add: () => setShowFibreDetection(true),
										remove: () => setShowFibreDetection(false)
									}}
								/>
							</Overlay>

							<Overlay
								name={(langdata && langdata.Lines) ? langdata.Lines : 'Lines'}
							>
								<Polyline
									positions={[]}
									eventHandlers={{
										add: () => setShowDetectionPolyline(true),
										remove: () => setShowDetectionPolyline(false)
									}}
								/>
							</Overlay>

							{/* ---- MIRROR (PORT) LINES ---- */}
							<Overlay
								name={(langdata && langdata.MirrorLines) ? langdata.MirrorLines : 'Mirror Lines'}
							>
								<Polyline
									positions={[]}
									eventHandlers={{
										add: () => (setShowPortPolyline(true), setShowPortPoints(true)),
										remove: () => (setShowPortPolyline(false))
									}}
								/>
							</Overlay>

							<Overlay
								name={(langdata && langdata.ShipFibreLink) ? langdata.ShipFibreLink : 'Ship-Fibre Link'}
							>
								<Polyline
									positions={[]}
									eventHandlers={{
										add: () => setShowShipFibreLink(true),
										remove: () => setShowShipFibreLink(false)
									}}
								/>
							</Overlay>
						</>
					)}
					{asnTrajectories.length > 0 && (
						<>
							<Overlay
								name={(langdata && langdata.AISPoints) ? langdata.AISPoints : 'AIS Points'}
							>
								<Polyline
									positions={[]}
									eventHandlers={{
										add: () => setShowAsnPoints(true),
										remove: () => setShowAsnPoints(false)
									}}
								/>
							</Overlay>

							<Overlay
								name={(langdata && langdata.AISLines) ? langdata.AISLines : 'AIS Lines'}
							>
								<Polyline
									positions={[]}
									eventHandlers={{
										add: () => setShowAsnLines(true),
										remove: () => setShowAsnLines(false)
									}}
								/>
							</Overlay>
						</>
					)}
					<Overlay
						name={(langdata && langdata.ReferenceCoordinates) ? langdata.ReferenceCoordinates : 'Reference Coordinates'}
					>
						<Polyline
							positions={[]}
							eventHandlers={{
								add: () => setShowReferenceCoordinate(true),
								remove: () => setShowReferenceCoordinate(false)
							}}
						/>
					</Overlay>

					<Overlay
						name={(langdata && langdata.CoordinateFibreLine) ? langdata.CoordinateFibreLine : 'Coordinate-Fibre Link'}
					>
						<Polyline
							positions={[]}
							eventHandlers={{
								add: () => setShowCoordinateFibreLine(true),
								remove: () => setShowCoordinateFibreLine(false)
							}}
						/>
					</Overlay>

					{layersControlChildren}

				</LayersControl>

				{mapChildren}

				{/* Distance range bands + S/E end points. */}
				<BulkLayer items={distanceRangeItems} />

				{hasTrack && (
					<>

						<Polyline
							positions={trackPositions}
							pathOptions={{
								color,
								weight: 3,
								opacity: 0.8
							}}
						/>

						{detectionTrack.map(
							(point, index) => {

								if (point.isCPA) {
									return null;
								}

								return (
									<CircleMarker
										key={`detection-pt-${index}`}
										center={[
											point.lat,
											point.lon
										]}
										radius={5}
										pathOptions={{
											color: '#ffffff',
											weight: 1,
											fillColor: color,
											fillOpacity: 0.9
										}}
									>
										<Tooltip
											direction="top"
											offset={[0, -4]}
										>
											<DetectionHoverContent
												info={detectionInfo}
												moreInfo={detectionTrajectories}
												point={point}
											/>
										</Tooltip>

										<Popup>
											<strong>
												{detectionLabel ||
													'Detection'}
											</strong>

											<br />

											Point #{index + 1}
											{' '}
											of
											{' '}
											{detectionTrack.length}

											<br />

											Lat:
											{' '}
											{point.lat.toFixed(6)}

											<br />

											Lon:
											{' '}
											{point.lon.toFixed(6)}

											{point.sbr !== null &&
												point.sbr !== undefined && (
												<>
													<br />
														SBR:
													{' '}
													{point.sbr}
												</>
											)}

											{point.sourceAngle !== null &&
												point.sourceAngle !== undefined && (
												<>
													<br />
														Source angle:
													{' '}
													{point.sourceAngle}°
												</>
											)}

											{point.maxBeamEnergy !== null &&
												point.maxBeamEnergy !== undefined && (
												<>
													<br />
														Max beam energy:
													{' '}
													{point.maxBeamEnergy}
												</>
											)}

											{point.sourceDistance !== null &&
												point.sourceDistance !== undefined && (
												<>
													<br />
														Source distance:
													{' '}
													{point.sourceDistance} m
												</>
											)}

											{point.timeStart && (
												<>
													<br />
													Start:
													{' '}
													{point.timeStart}
												</>
											)}

											{point.timeEnd && (
												<>
													<br />
													End:
													{' '}
													{point.timeEnd}
												</>
											)}
										</Popup>
									</CircleMarker>
								);
							}
						)}

						{firstPoint && (
							<Marker
								position={[
									firstPoint.lat,
									firstPoint.lon
								]}
								icon={labelIcon(
									'',
									'#16a34a'
								)}
							>
								<Tooltip
									direction="top"
									offset={[0, -10]}
								>
									<DetectionHoverContent
										info={detectionInfo}
										moreInfo={detectionTrajectories}
										point={firstPoint}
									/>
								</Tooltip>

								<Popup>
									Start of detection track
								</Popup>
							</Marker>
						)}

						{cpaPoint && (
							<Marker
								position={[
									cpaPoint && cpaPoint.lat,
									cpaPoint && cpaPoint.lon
								]}
								icon={labelIcon(
									'',
									''
								)}
							>
								<Tooltip
									direction="top"
									offset={[0, -40]}
								>
									<DetectionHoverContent
										info={detectionInfo}
										point={cpaPoint}
									/>
								</Tooltip>

								<Popup>
									<strong>
										{detectionLabel ||
											'Detection'}
									</strong>

									{cpaPoint && cpaPoint.sourceAngle !== null &&
										cpaPoint.sourceAngle !== undefined && (
										<>
											<br />
												Angle:
											{' '}
											{cpaPoint.sourceAngle}°
										</>
									)}

									{cpaPoint && cpaPoint.sbr !== null &&
										cpaPoint.sbr !== undefined && (
										<>
											<br />
												SBR:
											{' '}
											{cpaPoint.sbr}
										</>
									)}

									{detectionMeta &&
										detectionMeta.sourceSpeedKts !== undefined &&
										detectionMeta.sourceSpeedKts !== null && (
										<>
											<br />
												Speed:
											{' '}
											{detectionMeta.sourceSpeedKts}
											{' '}
												kn
										</>
									)}

									{detectionMeta &&
										detectionMeta.sourceSpeedms !== undefined &&
										detectionMeta.sourceSpeedms !== null && (
										<>
											<br />
												Speed:
											{' '}
											{detectionMeta.sourceSpeedms}
											{' '}
												m/s
										</>
									)}

									{detectionMeta &&
										detectionMeta.cpaSourceDistance !== undefined &&
										detectionMeta.cpaSourceDistance !== null && (
										<>
											<br />
												CPA distance:
											{' '}
											{detectionMeta.cpaSourceDistance}
											{' '}
												m
										</>
									)}

									{detectionMeta &&
										detectionMeta.cpaSourceDatetime && (
										<>
											<br />
												CPA time:
											{' '}
											{detectionMeta.cpaSourceDatetime}
										</>
									)}
								</Popup>
							</Marker>
						)}
					</>
				)}

				{/* All detection / port / fibre-detection points + markers (arrays
				    on one canvas) and detection / port / ship-fibre lines. */}
				<BulkLayer items={detectionItems} />

				{/* AIS (ASN) trajectories. */}
				<BulkLayer items={asnItems} />

				{playAllTrajectories && detectionTrajectories.map((trajectory) => {
					const animatedPoints = animatedTrajectoryPoints[trajectory.id] || [];
					if (!animatedPoints.length) return null;

					const headPosition = animatedPoints[animatedPoints.length - 1];

					return (
						<React.Fragment key={`play-trajectory-${trajectory.id}`}>
							<Polyline
								positions={animatedPoints}
								pathOptions={{ color: 'green', weight: 4, opacity: 0.9 }}
							/>

							<Marker position={headPosition} icon={vesselCpaIcon}>
								<Tooltip direction="top" offset={[0, -40]}>
									{trajectory.channelName || trajectory.label || (langdata && langdata.RealtimeDetection ? langdata.RealtimeDetection : 'Detection')}
								</Tooltip>
								<Popup>
									{trajectory.channelName || trajectory.label || (langdata && langdata.RealtimeDetection ? langdata.RealtimeDetection : 'Detection')}
								</Popup>
							</Marker>
						</React.Fragment>
					);
				})}

				<ScaleControl position="bottomleft" />

			</MapContainer>

			{showBottomInfoBar && <BottomInfoBar map={mapInstance} positionRef={positionSpanRef} />}
		</div>
	);
};

// ============================================================
// PROP TYPES
// ============================================================

SeaMap.propTypes = {
	center: PropTypes.oneOfType([
		PropTypes.arrayOf(
			PropTypes.number
		),
		PropTypes.object
	]),

	zoom: PropTypes.number,

	style: PropTypes.object,

	mapContainerProps:
		PropTypes.object,

	layersControlChildren:
		PropTypes.node,

	mapChildren:
		PropTypes.node,

	detectionTrack:
		PropTypes.arrayOf(
			PropTypes.shape({
				lat: PropTypes.number,
				lon: PropTypes.number,
				sbr: PropTypes.number,
				sourceAngle:
					PropTypes.number,
				maxBeamEnergy:
					PropTypes.number,
				sourceDistance:
					PropTypes.number,
				timeStart:
					PropTypes.string,
				timeEnd:
					PropTypes.string,
				isCPA:
					PropTypes.bool
			})
		),

	detectionMeta:
		PropTypes.shape({
			sourceSpeedms:
				PropTypes.number,
			sourceSpeedKts:
				PropTypes.number,
			cpaSourceDatetime:
				PropTypes.string,
			cpaSourceDistance:
				PropTypes.number,
			eventPositions:
				PropTypes.array
		}),

	detectionLabel:
		PropTypes.string,

	detectionInfo:
		PropTypes.shape({
			eventType:
				PropTypes.string,
			detectionAlgorithmTypeName:
				PropTypes.string,
			channelName:
				PropTypes.string,
			sourceSpeedms:
				PropTypes.number,
			pidsName:
				PropTypes.string
		}),

	trackColor:
		PropTypes.string,

	pipelineTracks:
		PropTypes.arrayOf(
			PropTypes.arrayOf(
				PropTypes.array
			)
		),

	fiberTracks:
		PropTypes.arrayOf(
			PropTypes.arrayOf(
				PropTypes.array
			)
		),

	pipelineBorderTracks:
		PropTypes.arrayOf(
			PropTypes.arrayOf(
				PropTypes.array
			)
		),

	optionalPathTracks:
		PropTypes.arrayOf(
			PropTypes.arrayOf(
				PropTypes.array
			)
		),

	kmpMarkers:
		PropTypes.arrayOf(
			PropTypes.shape({
				geocode: PropTypes.arrayOf(PropTypes.number),
				pop: PropTypes.string,
				landmarkDescription: PropTypes.string,
				pipelineChainage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			})
		),

	wbMarkers:
		PropTypes.arrayOf(
			PropTypes.shape({
				geocode: PropTypes.arrayOf(PropTypes.number),
				pop: PropTypes.string,
				landmarkDescription: PropTypes.string,
				pipelineChainage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			})
		),

	tpMarkers:
		PropTypes.arrayOf(
			PropTypes.shape({
				geocode: PropTypes.arrayOf(PropTypes.number),
				pop: PropTypes.string,
				landmarkDescription: PropTypes.string,
				pipelineChainage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			})
		),

	tlpMarkers:
		PropTypes.arrayOf(
			PropTypes.shape({
				geocode: PropTypes.arrayOf(PropTypes.number),
				pop: PropTypes.string,
				landmarkDescription: PropTypes.string,
				pipelineChainage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			})
		),

	chMarkers:
		PropTypes.arrayOf(
			PropTypes.shape({
				geocode: PropTypes.arrayOf(PropTypes.number),
				pop: PropTypes.string,
				landmarkDescription: PropTypes.string,
				pipelineChainage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			})
		),

	detectionTrajectories:
		PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.oneOfType([
					PropTypes.string,
					PropTypes.number
				]),

				color:
					PropTypes.string,

				label:
					PropTypes.string,

				eventType:
					PropTypes.string,

				channelName:
					PropTypes.string,

				algorithmName:
					PropTypes.string,

				detectionAlgorithmTypeName:
					PropTypes.string,

				detectionAlgorithmTypeId:
					PropTypes.oneOfType([
						PropTypes.string,
						PropTypes.number
					]),

				modelName:
					PropTypes.string,

				multipleModelName:
					PropTypes.string,

				sourceSpeedKts:
					PropTypes.oneOfType([
						PropTypes.number,
						PropTypes.arrayOf(PropTypes.number)
					]),

				sourceSpeedms:
					PropTypes.number,

				cpaSourceDistance:
					PropTypes.oneOfType([
						PropTypes.number,
						PropTypes.arrayOf(PropTypes.number)
					]),

				cpaSourceDatetime:
					PropTypes.string,
				pidsName:
					PropTypes.string,

				startTimeHover:
					PropTypes.oneOfType([
						PropTypes.string,
						PropTypes.number,
						PropTypes.array
					]),

				peakCoordinates:
					PropTypes.shape({
						latitude: PropTypes.oneOfType([
							PropTypes.string,
							PropTypes.number
						]),
						longitude: PropTypes.oneOfType([
							PropTypes.string,
							PropTypes.number
						]),
					}),

				points:
					PropTypes.arrayOf(
						PropTypes.shape({
							lat:
								PropTypes.number,

							lng:
								PropTypes.number,

							lon:
								PropTypes.number,

							sbr:
								PropTypes.number,

							sourceAngle:
								PropTypes.number,

							sourceDistance:
								PropTypes.number,

							isCPA:
								PropTypes.bool
						})
					),

				portPoints:
					PropTypes.arrayOf(
						PropTypes.shape({
							lat:
								PropTypes.number,

							lng:
								PropTypes.number,

							sbr:
								PropTypes.number,

							sourceAngle:
								PropTypes.number,

							index:
								PropTypes.number
						})
					)
			})
		),
	asnData: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
			longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
			speed: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
			datetime: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
		})
	),
	distanceRangeTracks:
		PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
				color: PropTypes.string,
				algorithmName: PropTypes.string,
				channelName: PropTypes.string,
				eventType: PropTypes.string,
				minDistance: PropTypes.number,
				maxDistance: PropTypes.number,
				positions: PropTypes.arrayOf(PropTypes.array),
				startPoint: PropTypes.shape({
					lat: PropTypes.number,
					lng: PropTypes.number,
					distance: PropTypes.number,
				}),
				endPoint: PropTypes.shape({
					lat: PropTypes.number,
					lng: PropTypes.number,
					distance: PropTypes.number,
				}),
			}),

		),


	autoCenter:
		PropTypes.bool,

	showTrajectoryPlayControl:
		PropTypes.bool,

	showFullscreenControl:
		PropTypes.bool,

	showTopToolbar:
		PropTypes.bool,

	showBottomInfoBar:
		PropTypes.bool,

	pidsRegionData:
		PropTypes.array,

	pigPids:
		PropTypes.array,

	hiddenTrajectoryCpaIds:
		PropTypes.array,

	enableAISVessels:
		PropTypes.bool,

	showAISVessels:
		PropTypes.bool,

	aisVesselsUrl:
		PropTypes.string,

	aisPollIntervalMs:
		PropTypes.number,

	aisBbox:
		PropTypes.arrayOf(PropTypes.number),

	aisIncludeKinds:
		PropTypes.arrayOf(PropTypes.string),

	aisMaxVessels:
		PropTypes.number,

	aisFallbackBbox:
		PropTypes.arrayOf(PropTypes.number),

	aisstreamApiKey:
		PropTypes.string,

	coordinatesData: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			name: PropTypes.string,
			latitude: PropTypes.number,
			longitude: PropTypes.number,
			channelName: PropTypes.string,
			pidsName: PropTypes.string,
			createdAt: PropTypes.string,
		})
	),

	onDeleteCoordinate:
		PropTypes.func,
};

// ============================================================
// DEFAULT PROPS
// ============================================================

SeaMap.defaultProps = {
	center: undefined,

	zoom: 11,

	style: undefined,

	mapContainerProps: {},

	layersControlChildren:
		null,

	mapChildren:
		null,

	detectionTrack: [],

	detectionMeta:
		null,

	detectionLabel:
		'',

	detectionInfo:
		null,

	trackColor:
		undefined,

	pipelineTracks:
		[],

	fiberTracks:
		[],

	pipelineBorderTracks:
		[],

	optionalPathTracks:
		[],

	kmpMarkers:
		[],

	wbMarkers:
		[],

	tpMarkers:
		[],

	tlpMarkers:
		[],

	chMarkers:
		[],

	detectionTrajectories:
		[],
	distanceRangeTracks:
		[],
	asnData: [],

	autoCenter:
		false,

	showTrajectoryPlayControl:
		true,

	showFullscreenControl:
		true,

	showTopToolbar:
		true,

	showBottomInfoBar:
		true,

	pidsRegionData:
		undefined,

	pigPids:
		[],

	hiddenTrajectoryCpaIds:
		[],

	enableAISVessels:
		true,

	showAISVessels:
		false,

	aisVesselsUrl:
		OPENWATERS_VESSELS_URL,

	aisPollIntervalMs:
		15000,

	aisBbox:
		undefined,

	aisIncludeKinds:
		['vessel'],

	aisMaxVessels:
		500,

	aisFallbackBbox:
		AIS_FALLBACK_BBOX,

	aisstreamApiKey:
		undefined,

	coordinatesData: [],

	onDeleteCoordinate:
		null,
};

// Memoized so SeaMap only re-renders when one of its props actually
// changes identity (parents pass stable, memoized props).
export default React.memo(SeaMap);